// ScaleUpBusinessScreen.js — Company Scale-Up Strategy Hub — Full Feature Build
/* eslint-disable no-unused-vars */
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Modal, Animated, Dimensions, Alert, RefreshControl,
  ActivityIndicator, Share, Platform, Switch, FlatList, BackHandler,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import {
  MaterialCommunityIcons, Ionicons, FontAwesome5, Feather,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useFocusEffect } from '@react-navigation/native';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

// ── Cross-screen services ──────────────────────────────────────────────────
let aiServiceRef = null;
try { aiServiceRef = require('../../../services/aiService'); } catch {}
const singleCompletion = aiServiceRef?.singleCompletion || null;
const getActiveProvider = aiServiceRef?.getActiveProvider || (() => ({ name: 'Claude AI' }));

const { width, height } = Dimensions.get('window');

// ── API ────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  strategy:    `${API_BASE_URL}/api/data/strategic_plans`,
  metrics:     `${API_BASE_URL}/api/data/growth_metrics`,
  milestones:  `${API_BASE_URL}/api/data/company_milestones`,
  initiatives: `${API_BASE_URL}/api/data/strategic_initiatives`,
  markets:     `${API_BASE_URL}/api/data/market_analysis`,
  competitors: `${API_BASE_URL}/api/data/competitor_analysis`,
  finances:    `${API_BASE_URL}/api/data/financial_forecasts`,
  team:        `${API_BASE_URL}/api/data/team_scaling_plans`,
  risks:       `${API_BASE_URL}/api/data/risk_register`,
  okrs:        `${API_BASE_URL}/api/data/okrs`,
  funding:     `${API_BASE_URL}/api/data/funding_rounds`,
};
let CACHE_KEY = 'scaleup_cache_v2';
const CACHE_TTL = 15 * 60 * 1000;

const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json' } });
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-API-Key'] = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
    const cid = await AsyncStorage.getItem('company_id');
    if (cid) config.params = { ...config.params, company_id: parseInt(cid) };
  } catch {}
  return config;
});

// ── BRAND ──────────────────────────────────────────────────────────────────
const T = {
  primary: '#0e4e96', secondary: '#19abb5', accent: '#fdb913',
  success: '#10b981', warning: '#f59e0b', danger: '#ef4444',
  purple: '#8b5cf6', indigo: '#6366f1', pink: '#ec4899', orange: '#f97316',
  teal: '#14b89c', dark: '#0f172a', gray: '#475569', light: '#f8fafc',
  white: '#ffffff', border: '#e2e8f0', bg: '#f1f5f9',
  claude: '#b06939',
};

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const GROWTH_PHASES = [
  { id: 'foundation',    name: 'Foundation',    color: '#6366f1', icon: 'layers-outline',          gradient: ['#6366f1','#4f46e5'] },
  { id: 'validation',    name: 'Validation',    color: '#14b89c', icon: 'checkmark-done-outline',  gradient: ['#14b89c','#0d9488'] },
  { id: 'acceleration',  name: 'Acceleration',  color: '#f97316', icon: 'rocket-outline',           gradient: ['#f97316','#ea580c'] },
  { id: 'expansion',     name: 'Expansion',     color: '#8b5cf6', icon: 'globe-outline',            gradient: ['#8b5cf6','#7c3aed'] },
  { id: 'dominance',     name: 'Dominance',     color: '#ec4899', icon: 'trophy-outline',           gradient: ['#ec4899','#db2777'] },
];

const INITIATIVE_TYPES = [
  { id: 'market',      name: 'Market Expansion', icon: 'map-marker-multiple',  color: T.primary,  gradient: [T.primary,  '#2563eb'] },
  { id: 'product',     name: 'Product Dev',      icon: 'cube',                 color: T.teal,     gradient: [T.teal,     '#0d9488'] },
  { id: 'team',        name: 'Team Scaling',      icon: 'account-group',        color: T.warning,  gradient: [T.warning,  '#d97706'] },
  { id: 'tech',        name: 'Technology',        icon: 'chip',                 color: T.indigo,   gradient: [T.indigo,   '#4f46e5'] },
  { id: 'funding',     name: 'Funding',           icon: 'cash-multiple',        color: T.success,  gradient: [T.success,  '#059669'] },
  { id: 'partnership', name: 'Partnerships',      icon: 'handshake',            color: T.purple,   gradient: [T.purple,   '#7c3aed'] },
];

const RISK_LEVELS  = ['Critical','High','Medium','Low'];
const RISK_COLORS  = { Critical: T.danger, High: T.orange, Medium: T.warning, Low: T.success };
const RISK_CATS    = ['Market','Financial','Operational','Technical','Legal','People','External'];
const OKR_PERIODS  = ['Q1 2025','Q2 2025','Q3 2025','Q4 2025','FY 2025','FY 2026'];
const FUNDING_STAGES = ['Pre-Seed','Seed','Series A','Series B','Series C','Growth','IPO'];
const PRIORITY_LEVELS = [
  { id: 'critical', name: 'Critical', color: T.danger  },
  { id: 'high',     name: 'High',     color: T.orange  },
  { id: 'medium',   name: 'Medium',   color: T.warning },
  { id: 'low',      name: 'Low',      color: T.success },
];

// ── MOCK DATA ──────────────────────────────────────────────────────────────
// MOCK data removed — screen uses EMPTY defaults + real API data



// Empty defaults matching MOCK shape — prevents render crashes
const EMPTY = {
  metrics: { revenue:{current:0,target:0,growth:0}, customers:{current:0,target:0,growth:0}, mrr:{current:0,target:0,growth:0}, cac:{current:0,target:0,growth:0}, ltv:{current:0,target:0,growth:0}, burnRate:0, runway:0, marketShare:0, teamSize:0, nps:0, churnRate:0, grossMargin:0 },
  milestones: [],
  initiatives: [],
  markets: [],
  competitors: [],
  team: { total:0, engineering:0, sales:0, product:0, operations:0, finance:0, growthTarget:0 },
  risks: [],
  okrs: [],
  funding: [],
  financialForecast: { years:[], revenue:[], mrr:[], customers:[], headcount:[], grossMargin:[] },
  techRoadmap: [],
  strategy: { vision:'', mission:'', values:[], goals:[] },
};

// ── HELPERS ────────────────────────────────────────────────────────────────
const fmtDate  = (d) => { try { return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); } catch { return '—'; }};
const fmtMoney = (n) => { if (n >= 1000000) return `£${(n/1000000).toFixed(1)}M`; if (n >= 1000) return `£${(n/1000).toFixed(0)}K`; return `£${n||0}`; };
const daysLeft = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);
const pct      = (c, t) => t > 0 ? Math.min(100, Math.round((c / t) * 100)) : 0;
const riskScore= (r) => Math.round((r.probability / 100) * (r.impact / 100) * 100);

// ─────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────
export default function ScaleUpBusinessScreen({ navigation }) {

  // ── Core data ────────────────────────────────────────────────────────────
  const [metrics,       setMetrics]       = useState(EMPTY.metrics);
  const [milestones,    setMilestones]    = useState(EMPTY.milestones);
  const [initiatives,   setInitiatives]   = useState(EMPTY.initiatives);
  const [markets,       setMarkets]       = useState(EMPTY.markets);
  const [competitors,   setCompetitors]   = useState(EMPTY.competitors);
  const [teamData,      setTeamData]      = useState(EMPTY.team);
  const [risks,         setRisks]         = useState(EMPTY.risks);
  const [okrs,          setOkrs]          = useState(EMPTY.okrs);
  const [funding,       setFunding]       = useState(EMPTY.funding);
  const [forecast,      setForecast]      = useState(EMPTY.financialForecast);
  const [techRoadmap,   setTechRoadmap]   = useState(EMPTY.techRoadmap);
  const [strategy,      setStrategy]      = useState(EMPTY.strategy);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [loading,       setLoading]       = useState(true);
  const [companyName,   setCompanyName]   = useState('');
  const [refreshing,    setRefreshing]    = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('ScaleUpBusinessScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [activePhase,   setActivePhase]   = useState('all');
  const [isOnline,      setIsOnline]      = useState(true);
  const [error,         setError]         = useState(null);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [showSearch,    setShowSearch]    = useState(false);
  const [aiProvider,    setAiProvider]    = useState('Claude AI');

  // ── Modal visibility ──────────────────────────────────────────────────────
  const [showAddInitiative,  setShowAddInitiative]  = useState(false);
  const [showAddMilestone,   setShowAddMilestone]   = useState(false);
  const [showStrategy,       setShowStrategy]       = useState(false);
  const [showAnalytics,      setShowAnalytics]      = useState(false);
  const [showRisks,          setShowRisks]          = useState(false);
  const [showInvestorDeck,   setShowInvestorDeck]   = useState(false);
  const [showOKRs,           setShowOKRs]           = useState(false);
  const [showFunding,        setShowFunding]        = useState(false);
  const [showMarkets,        setShowMarkets]        = useState(false);
  const [showCompetitors,    setShowCompetitors]    = useState(false);
  const [showInitDetail,     setShowInitDetail]     = useState(false);
  const [showMilestoneDetail,setShowMilestoneDetail]= useState(false);
  const [showTeamPlan,       setShowTeamPlan]       = useState(false);
  const [showForecast,       setShowForecast]       = useState(false);
  const [showAIAdvisor,      setShowAIAdvisor]      = useState(false);
  const [showAddRisk,        setShowAddRisk]        = useState(false);
  const [showAddOKR,         setShowAddOKR]         = useState(false);
  const [showAddFunding,     setShowAddFunding]     = useState(false);

  // ── Selected items ────────────────────────────────────────────────────────
  const [selectedInit,  setSelectedInit]  = useState(null);
  const [selectedMs,    setSelectedMs]    = useState(null);
  const [selectedRisk,  setSelectedRisk]  = useState(null);

  // ── AI state ─────────────────────────────────────────────────────────────
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [aiOutput,      setAiOutput]      = useState('');
  const [aiPrompt,      setAiPrompt]      = useState('');
  const [deckContent,   setDeckContent]   = useState('');
  const [deckLoading,   setDeckLoading]   = useState(false);

  // ── Forms ─────────────────────────────────────────────────────────────────
  const defaultInitForm = {
    title:'', type:'market', description:'', phase:'foundation', priority:'medium',
    owner:'', budget:'', start_date:new Date(),
    end_date:new Date(Date.now()+90*86400000),
    success_metrics:'', risks:'', notes:[],
  };
  const [initForm,      setInitForm]      = useState(defaultInitForm);
  const defaultMsForm = {
    title:'', phase:'foundation', target_date:new Date(Date.now()+30*86400000),
    description:'', success_criteria:'', priority:'medium',
  };
  const [msForm,        setMsForm]        = useState(defaultMsForm);
  const [riskForm,      setRiskForm]      = useState({ title:'', category:'Market', level:'Medium', probability:50, impact:50, mitigation:'', owner:'' });
  const [okrForm,       setOkrForm]       = useState({ period:'Q2 2025', objective:'', key_results:[{text:'',current:0,target:100,unit:'%'}] });
  const [fundingForm,   setFundingForm]   = useState({ stage:'Series A', amount:'', currency:'£', date:'', investors:'', equity:'', valuation:'' });

  // ── Animations ────────────────────────────────────────────────────────────
  const scrollY  = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const pulse    = useRef(new Animated.Value(1)).current;

  // ─────────────────────────────────────────────────────────────────────────
  // INIT
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const prov = getActiveProvider();
        setAiProvider(prov?.name || 'Claude AI');
      } catch {}
      Animated.timing(fadeAnim, { toValue:1, duration:600, useNativeDriver:true }).start();
      Animated.loop(Animated.sequence([
        Animated.timing(pulse, { toValue:1.08, duration:900, useNativeDriver:true }),
        Animated.timing(pulse, { toValue:1,    duration:900, useNativeDriver:true }),
      ])).start();
      await loadAll();
    };
    init();
    const back = BackHandler.addEventListener('hardwareBackPress', () => { navigation?.goBack(); return true; });
    return () => back.remove();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────
  const loadAll = async () => { const _cid = await AsyncStorage.getItem('company_id'); CACHE_KEY = 'scaleup_cache_v2_' + (_cid||'');
      try { const cp = await AsyncStorage.getItem('company_profile'); if (cp) { const p = JSON.parse(cp); setCompanyName(p.name || p.company_name || ''); } } catch {}
    setLoading(true);
    const cid = await AsyncStorage.getItem('company_id');
    CACHE_KEY = 'scaleup_cache_v2_' + (cid || '');
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const { ts, data } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          applyData(data);
          setLoading(false);
          setRefreshing(false);
          fetchFresh();
          return;
        }
      }
    } catch {}
    await fetchFresh();
    setLoading(false);
    setRefreshing(false);
  };

  const fetchFresh = async () => {
    try {
      const results = await Promise.allSettled([
        fetchMetrics(), fetchMilestones(), fetchInitiatives(),
        fetchMarkets(), fetchCompetitors(), fetchTeam(), fetchStrategy(),
      ]);
      setError(null);
    } catch (e) {
      setError('Some live data unavailable — showing cached data');
    }
  };

  const applyData = (d) => {
    if (d.metrics)    setMetrics(d.metrics);
    if (d.milestones) setMilestones(d.milestones);
    if (d.initiatives)setInitiatives(d.initiatives);
    if (d.markets)    setMarkets(d.markets);
    if (d.competitors)setCompetitors(d.competitors);
    if (d.team)       setTeamData(d.team);
    if (d.strategy)   setStrategy(d.strategy);
  };

  const saveCache = async (partialData) => {
    try {
      const existing = JSON.parse(await AsyncStorage.getItem(CACHE_KEY) || '{}');
      const merged = { ts: Date.now(), data: { ...existing.data, ...partialData } };
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(merged));
    } catch {}
  };

  const fetchMetrics = async () => {
    try {
      const r = await api.get(API_ENDPOINTS.metrics);
      if (r.data?.success && Array.isArray(r.data.data) && r.data.data.length > 0) {
        const obj = { revenue:{current:0,target:0,growth:0}, mrr:{current:0,target:0,growth:0}, customers:{current:0,target:0,growth:0}, cac:{current:0,target:0}, ltv:{current:0,target:0}, burnRate:0, runway:0, marketShare:0, teamSize:0, nps:0, churnRate:0, grossMargin:0 };
        r.data.data.forEach(m => {
          const n = (m.metric_name||'').toLowerCase();
          if (n.includes('revenue'))  obj.revenue   = { current:+m.current_value||0, target:+m.target_value||0, growth:+m.growth_rate||0 };
          if (n.includes('customer')) obj.customers  = { current:+m.current_value||0, target:+m.target_value||0, growth:+m.growth_rate||0 };
          if (n.includes('mrr'))      obj.mrr        = { current:+m.current_value||0, target:+m.target_value||0, growth:+m.growth_rate||0 };
          if (n.includes('burn'))     obj.burnRate   = +m.current_value||0;
          if (n.includes('runway'))   obj.runway     = +m.current_value||0;
        });
        setMetrics(obj); saveCache({ metrics:obj });
        return;
      }
    } catch {}
    // Fallback: build metrics from real tables
    try {
      const [empR, invR, expR, cliR] = await Promise.allSettled([
        api.get('/api/data/employees'), api.get('/api/data/financial_invoices'),
        api.get('/api/data/financial_expenses'), api.get('/api/data/clients'),
      ]);
      const emps = empR.status==='fulfilled' ? (empR.value?.data?.data||[]) : [];
      const invs = invR.status==='fulfilled' ? (invR.value?.data?.data||[]) : [];
      const exps = expR.status==='fulfilled' ? (expR.value?.data?.data||[]) : [];
      const clis = cliR.status==='fulfilled' ? (cliR.value?.data?.data||[]) : [];
      const rev = invs.reduce((s,i) => s + parseFloat(i.total_amount||i.amount||0), 0);
      const exp = exps.reduce((s,e) => s + parseFloat(e.amount||0), 0);
      const obj = {
        revenue:   { current:rev, target:rev*2, growth: rev>0?20:0 },
        customers: { current:clis.length, target:clis.length*2, growth:0 },
        mrr:       { current:Math.round(rev/12), target:Math.round(rev/6), growth:0 },
        cac:       { current:0, target:0 }, ltv: { current:0, target:0 },
        burnRate: Math.round(exp/12), runway: exp>0 ? Math.round((rev-exp)/(exp/12)) : 0,
        marketShare:0, teamSize:emps.length, nps:0, churnRate:0, grossMargin: rev>0?Math.round((rev-exp)/rev*100):0,
      };
      setMetrics(obj); saveCache({ metrics:obj });
    } catch {}
  };

  const fetchMilestones = async () => {
    const r = await api.get(API_ENDPOINTS.milestones);
    if (r.data?.success && Array.isArray(r.data.data)) {
      const d = r.data.data.map(i => ({ id:String(i.id), title:i.title||'Milestone', phase:i.phase||'foundation', target_date:i.target_date||new Date().toISOString(), description:i.description||'', success_criteria:i.success_criteria||'', completed:i.status==='completed'||i.progress===100||false, priority:i.priority||'medium' }));
      setMilestones(d); saveCache({ milestones:d });
    }
  };

  const fetchInitiatives = async () => {
    const r = await api.get(API_ENDPOINTS.initiatives);
    if (r.data?.success && Array.isArray(r.data.data)) {
      const d = r.data.data.map(i => ({ id:String(i.id), title:i.title||'Initiative', type:i.type||'market', description:i.description||'', phase:i.phase||'foundation', priority:i.priority||'medium', owner:i.owner||'', budget:+i.budget||0, start_date:i.start_date||new Date().toISOString(), end_date:i.end_date||new Date().toISOString(), progress:+i.progress||0, status:i.status||'planning', success_metrics:i.success_metrics||'', risks:i.risks||'', notes:i.notes||[] }));
      setInitiatives(d); saveCache({ initiatives:d });
    }
  };

  const fetchMarkets  = async () => { const r = await api.get(API_ENDPOINTS.markets);     if (r.data?.success) { const d = r.data.data.map(i=>({id:String(i.id),name:i.name||'Market',status:i.status||'Exploring',potential:i.potential||'Medium',competition:i.competition||'Medium',share:i.share||'0%',size:i.size||'—',notes:i.notes||''})); setMarkets(d); saveCache({markets:d}); } };
  const fetchCompetitors = async () => { const r = await api.get(API_ENDPOINTS.competitors); if (r.data?.success) { const d = r.data.data.map(i=>({id:String(i.id),name:i.name||'Competitor',position:i.position||'Challenger',strengths:Array.isArray(i.strengths)?i.strengths:[],weaknesses:Array.isArray(i.weaknesses)?i.weaknesses:[],threat:i.threat||'Medium',ourAdvantage:i.ourAdvantage||''})); setCompetitors(d); saveCache({competitors:d}); } };
  const fetchTeam = async () => {
    try {
      const r = await api.get(API_ENDPOINTS.team);
      if (r.data?.success && r.data.data?.length) {
        const d = r.data.data[0];
        setTeamData({ total:+d.current_headcount||0, engineering:+d.engineering_count||0, sales:+d.sales_count||0, product:+d.product_count||0, operations:+d.operations_count||0, finance:+d.finance_count||0, growthTarget:+d.hiring_target||25 });
        return;
      }
    } catch {}
    // Fallback: count from employees table
    try {
      const r = await api.get('/api/data/employees');
      const emps = r.data?.data || [];
      const byDept = {};
      emps.forEach(e => { const d = (e.department||'Other').toLowerCase(); byDept[d] = (byDept[d]||0)+1; });
      setTeamData({
        total: emps.length, engineering: byDept['engineering']||byDept['tech']||byDept['it']||0,
        sales: byDept['sales']||byDept['business development']||0, product: byDept['product']||0,
        operations: byDept['operations']||byDept['ops']||0, finance: byDept['finance']||byDept['accounting']||0,
        growthTarget: 25,
      });
    } catch {}
  };
  const fetchStrategy = async () => { const r = await api.get(API_ENDPOINTS.strategy); if (r.data?.success && r.data.data?.length) { const d = r.data.data[0]; setStrategy({ vision:d.vision||'', mission:d.mission||'', values:d.values||[], goals:d.goals||[] }); } };

  const onRefresh = async () => { setRefreshing(true); await loadAll(); };

  // ─────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────
  const createInitiative = async () => {
    if (!initForm.title.trim()) { Alert.alert('Error','Title required'); return; }
    const payload = { ...initForm, id:String(Date.now()), start_date:initForm.start_date.toISOString(), end_date:initForm.end_date.toISOString(), progress:0, status:'planning', created_at:new Date().toISOString() };
    try {
      const r = await api.post(API_ENDPOINTS.initiatives, payload);
      if (r.data?.success) payload.id = String(r.data.data.id||payload.id);
    } catch {}
    setInitiatives(p => [payload, ...p]);
    setShowAddInitiative(false); setInitForm(defaultInitForm);
    Alert.alert('✅ Created','Initiative added to your strategy');
  };

  const createMilestone = async () => {
    if (!msForm.title.trim()) { Alert.alert('Error','Title required'); return; }
    const payload = { ...msForm, id:String(Date.now()), target_date:msForm.target_date.toISOString(), completed:false, created_at:new Date().toISOString() };
    try {
      const r = await api.post(API_ENDPOINTS.milestones, payload);
      if (r.data?.success) payload.id = String(r.data.data.id||payload.id);
    } catch {}
    setMilestones(p => [payload, ...p]);
    setShowAddMilestone(false); setMsForm(defaultMsForm);
  };

  const toggleMilestone = async (id) => {
    setMilestones(p => p.map(m => m.id===id ? {...m, completed:!m.completed} : m));
    try { await api.put(`${API_ENDPOINTS.milestones}/${id}`, { completed:!milestones.find(m=>m.id===id)?.completed }); } catch {}
  };

  const deleteInitiative = (id) => Alert.alert('Delete?','Remove this initiative?',[
    { text:'Cancel', style:'cancel' },
    { text:'Delete', style:'destructive', onPress: async () => {
      setInitiatives(p => p.filter(i => i.id!==id));
      try { await api.delete(`${API_ENDPOINTS.initiatives}/${id}`); } catch {}
    }},
  ]);

  const deleteRisk = (id) => setRisks(p => p.filter(r => r.id!==id));

  const createRisk = () => {
    if (!riskForm.title.trim()) { Alert.alert('Error','Title required'); return; }
    setRisks(p => [{ ...riskForm, id:String(Date.now()), status:'active' }, ...p]);
    setShowAddRisk(false); setRiskForm({ title:'', category:'Market', level:'Medium', probability:50, impact:50, mitigation:'', owner:'' });
  };

  const createOKR = () => {
    if (!okrForm.objective.trim()) { Alert.alert('Error','Objective required'); return; }
    setOkrs(p => [{ ...okrForm, id:String(Date.now()) }, ...p]);
    setShowAddOKR(false); setOkrForm({ period:'Q2 2025', objective:'', key_results:[{text:'',current:0,target:100,unit:'%'}] });
  };

  const createFunding = () => {
    if (!fundingForm.amount) { Alert.alert('Error','Amount required'); return; }
    setFunding(p => [{ ...fundingForm, id:String(Date.now()), amount:+fundingForm.amount, equity:+fundingForm.equity||0, valuation:+fundingForm.valuation||0 }, ...p]);
    setShowAddFunding(false); setFundingForm({ stage:'Series A', amount:'', currency:'£', date:'', investors:'', equity:'', valuation:'' });
  };

  const updateInitProgress = (id, val) => setInitiatives(p => p.map(i => i.id===id ? {...i, progress:val} : i));

  // ─────────────────────────────────────────────────────────────────────────
  // AI FEATURES
  // ─────────────────────────────────────────────────────────────────────────
  const runAI = async (prompt) => {
    if (!singleCompletion) { setAiOutput('AI service not configured. Please check your aiService.js setup.'); return; }
    setStrategyLoading(true); setAiOutput('');
    try {
      const result = await singleCompletion(prompt, { maxTokens:1500 });
      setAiOutput(result || 'No response generated');
    } catch (e) {
      setAiOutput(`Error: ${e.message}`);
    } finally { setStrategyLoading(false); }
  };

  const generateInvestorDeck = async () => {
    setDeckLoading(true); setDeckContent('');
    const prompt = `Generate a comprehensive investor deck outline for ${companyName || 'our company'}.\n\nKey metrics:\n- ARR: ${fmtMoney(metrics.revenue?.current)}\n- Customers: ${metrics.customers?.current}\n- MRR: ${fmtMoney(metrics.mrr?.current)}\n- Growth: ${metrics.revenue?.growth}% YoY\n- Market Share: ${metrics.marketShare}%\n- Team: ${(teamData?.total||0)} people\n- Runway: ${metrics.runway} months\n\nVision: ${strategy.vision}\n\nCreate a 10-slide investor deck outline with: 1) Executive Summary, 2) Problem, 3) Solution & AI Differentiation, 4) Market Size (TAM/SAM/SOM), 5) Business Model, 6) Traction & Metrics, 7) Growth Strategy, 8) Team, 9) Financial Projections, 10) Ask & Use of Funds.\n\nFor each slide include: slide title, 3-4 key bullet points with specific numbers where possible, and a speaker note. Format clearly.`;
    try {
      if (singleCompletion) {
        const r = await singleCompletion(prompt, { maxTokens:2500 });
        setDeckContent(r||'Unable to generate deck');
      } else {
        setDeckContent(`${companyName || 'Company'} — Business Overview\nInvestor Deck\n\n📊 SLIDE 1: EXECUTIVE SUMMARY\n• ${fmtMoney(metrics.revenue?.current)} Annual Revenue\n• ${metrics.customers?.current || 0} customers\n• Team: ${(teamData?.total||0)} people\n• Growth: ${metrics.revenue?.growth || 0}% YoY\n\n📊 SLIDE 2: KEY METRICS\n• MRR: ${fmtMoney(metrics.mrr?.current)}\n• Gross Margin: ${metrics.grossMargin || 0}%\n• Runway: ${metrics.runway || 0} months\n• Market Share: ${metrics.marketShare || 0}%\n\n📊 SLIDE 3: STRATEGY\n• Vision: ${strategy.vision || 'Not set'}\n• Mission: ${strategy.mission || 'Not set'}\n• Active Initiatives: ${(initiatives||[]).length}\n• Milestones: ${(milestones||[]).filter(m=>m.completed).length}/${(milestones||[]).length} completed\n\n📊 SLIDE 4: MARKET POSITION\n• Markets: ${(markets||[]).length} active/planned\n• Competitors tracked: ${(competitors||[]).length}\n\n📊 SLIDE 5: TEAM & GROWTH\n• Current team: ${(teamData?.total||0)}\n• Growth target: +${(teamData?.growthTarget||0)}%\n\n📊 SLIDE 6: RISKS & MITIGATION\n• ${(risks||[]).filter(r=>r.level==='Critical').length} critical risks identified\n• ${(risks||[]).filter(r=>r.status==='mitigated').length} mitigated\n\nGenerated: ${new Date().toLocaleDateString('en-GB')}`);
      }
    } catch (e) { setDeckContent(`Error generating deck: ${e.message}`); }
    setDeckLoading(false);
  };

  const shareText = async (content, title) => {
    try { await Share.share({ message:content, title }); } catch {}
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTED
  // ─────────────────────────────────────────────────────────────────────────
  const filteredInit = useMemo(() => {
    let list = (initiatives||[]).filter(i => activePhase==='all' || i.phase===activePhase);
    if (searchQuery) list = list.filter(i => String(i.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) || String(i.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()));
    return list;
  }, [initiatives, activePhase, searchQuery]);

  const filteredMs = useMemo(() => {
    let list = (milestones||[]).filter(m => activePhase==='all' || m.phase===activePhase);
    if (searchQuery) list = list.filter(m => String(m.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()));
    return list;
  }, [milestones, activePhase, searchQuery]);

  const totalBudget   = initiatives.reduce((s,i) => s+(i.budget||0), 0);
  const activeCount   = (initiatives||[]).filter(i => i.status==='active').length;
  const completedMs   = (milestones||[]).filter(m => m.completed).length;
  const criticalRisks = (risks||[]).filter(r => r.level==='Critical' && r.status!=='mitigated').length;
  const avgOKRProgress = (okrs||[]).length ? Math.round(okrs.reduce((s,o) => s + (o.key_results.reduce((ss,kr)=>ss+pct(kr.current,kr.target),0)/Math.max(1,o.key_results.length)), 0) / (okrs||[]).length) : 0;

  const headerOp = scrollY.interpolate({ inputRange:[0,100], outputRange:[1,0.93], extrapolate:'clamp' });

  // ─────────────────────────────────────────────────────────────────────────
  // LOADING SCREEN
  // ─────────────────────────────────────────────────────────────────────────
  if (loading && !refreshing) return (
    <View style={S.loadingWrap}>
      <Animated.View style={{ opacity:fadeAnim, alignItems:'center' }}>
        <LinearGradient colors={[T.primary, T.indigo]} style={S.loadingIcon}>
          <MaterialCommunityIcons name="rocket-launch" size={48} color="#fff" />
        </LinearGradient>
        <Text style={S.loadingTitle}>Scale-Up Strategy</Text>
        <Text style={S.loadingSub}>Loading your growth platform…</Text>
        <ActivityIndicator size="small" color={T.primary} style={{ marginTop:20 }} />
      </Animated.View>
    </View>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RETURN
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={S.container}>
      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <Animated.View style={[S.header, { opacity:headerOp }]}>
        <LinearGradient colors={[T.primary, T.indigo]} start={{x:0,y:0}} end={{x:1,y:0}} style={S.headerGrad}>
          <View style={S.headerRow}>
            <TouchableOpacity style={S.headerBtn} onPress={() => navigation?.goBack()}>
              <Ionicons name="chevron-back" size={24} color="#fff" />
            </TouchableOpacity>
            <View style={{ flex:1, marginLeft:12 }}>
              <Text style={S.headerTitle}>Scale-Up Strategy</Text>
              <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
                <View style={{ width:7, height:7, borderRadius:4, backgroundColor:T.success }} />
                <Text style={S.headerSub}>{fmtMoney(metrics.revenue?.current)} ARR · {activeCount} Active</Text>
              </View>
            </View>
            <View style={{ flexDirection:'row', gap:6 }}>
              <TouchableOpacity style={S.headerBtn} onPress={() => setShowSearch(!showSearch)}>
                <Ionicons name="search" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={S.headerBtn} onPress={() => setShowAIAdvisor(true)}>
                <MaterialCommunityIcons name="brain" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={[S.headerBtn, { backgroundColor:T.accent }]} onPress={() => setShowAddInitiative(true)}>
                <Ionicons name="add" size={22} color="#fff" />
              </TouchableOpacity>
            </View>
          </View>

          {/* search bar */}
          {showSearch && (
            <View style={{ flexDirection:'row', alignItems:'center', backgroundColor:'rgba(255,255,255,0.15)', borderRadius:12, paddingHorizontal:14, paddingVertical:8, marginTop:10, gap:10 }}>
              <Ionicons name="search" size={16} color="rgba(255,255,255,0.7)" />
              <TextInput
                value={searchQuery} onChangeText={setSearchQuery}
                placeholder="Search initiatives, milestones…" placeholderTextColor="rgba(255,255,255,0.5)"
                style={{ flex:1, fontSize:14, color:'#fff' }} />
              {searchQuery ? <TouchableOpacity onPress={()=>setSearchQuery('')}><Ionicons name="close-circle" size={18} color="rgba(255,255,255,0.7)" /></TouchableOpacity> : null}
            </View>
          )}

          {/* 5-stat strip */}
          <View style={S.statsStrip}>
            {[
              { label:'ARR',          value:fmtMoney(metrics.revenue?.current), color:'#93c5fd' },
              { label:'Customers',    value:String(metrics.customers?.current), color:'#a7f3d0' },
              { label:'MRR Growth',   value:`+${metrics.mrr?.growth||0}%`,      color:'#fde68a' },
              { label:'Runway',       value:`${metrics.runway||0}mo`,           color:'#c4b5fd' },
              { label:'Market Share', value:`${metrics.marketShare||0}%`,       color:'#f9a8d4' },
            ].map((s,i) => (
              <React.Fragment key={i}>
                {i>0 && <View style={{ width:1, height:32, backgroundColor:'rgba(255,255,255,0.25)' }} />}
                <View style={{ flex:1, alignItems:'center' }}>
                  <Text style={{ fontSize:15, fontWeight:'800', color:s.color }}>{s.value}</Text>
                  <Text style={{ fontSize:9, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{s.label}</Text>
                </View>
              </React.Fragment>
            ))}
          </View>
        </LinearGradient>
      </Animated.View>

      {/* error banner */}
      {error && (
        <View style={S.errBanner}>
          <Ionicons name="warning-outline" size={18} color="#fff" />
          <Text style={{ flex:1, color:'#fff', fontSize:13, marginLeft:8 }}>{error}</Text>
          <TouchableOpacity onPress={loadAll}><Text style={{ color:T.accent, fontWeight:'700' }}>Retry</Text></TouchableOpacity>
        </View>
      )}

      {/* ── MAIN SCROLL ────────────────────────────────────────────────── */}
      <Animated.ScrollView
        style={{ opacity:fadeAnim }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={T.primary} colors={[T.primary]} />}
        onScroll={Animated.event([{ nativeEvent:{ contentOffset:{ y:scrollY } } }],{ useNativeDriver:true })}
        scrollEventThrottle={16}>

        {/* alert bar: critical risks */}
        {criticalRisks > 0 && (
          <TouchableOpacity onPress={() => setShowRisks(true)}
            style={{ flexDirection:'row', alignItems:'center', gap:10, backgroundColor:T.danger+'18', marginTop:100, marginHorizontal:16, borderRadius:12, padding:12, borderWidth:1.5, borderColor:T.danger+'40' }}>
            <View style={{ width:32, height:32, borderRadius:16, backgroundColor:T.danger+'20', alignItems:'center', justifyContent:'center' }}>
              <Ionicons name="warning" size={18} color={T.danger} />
            </View>
            <View style={{ flex:1 }}>
              <Text style={{ fontSize:13, fontWeight:'800', color:T.danger }}>{criticalRisks} Critical Risk{criticalRisks>1?'s':''} Require Attention</Text>
              <Text style={{ fontSize:11, color:T.gray }}>Tap to view risk register</Text>
            </View>
            <Ionicons name="chevron-forward" size={16} color={T.danger} />
          </TouchableOpacity>
        )}

        {/* 4-stat overview cards */}
        <View style={[S.statsRow, !criticalRisks && { marginTop:104 }]}>
          {[
            { label:'Active Initiatives', value:String(activeCount),     icon:'rocket',       grad:[T.primary,'#2563eb'] },
            { label:'Milestones',         value:`${completedMs}/${(milestones||[]).length}`,icon:'flag',grad:[T.teal,T.success] },
            { label:'Budget Deployed',    value:fmtMoney(totalBudget),   icon:'cash',         grad:[T.purple,'#7c3aed'] },
            { label:'OKR Progress',       value:`${avgOKRProgress}%`,    icon:'trophy',       grad:[T.orange,'#ea580c'] },
          ].map((s,i) => (
            <LinearGradient key={i} colors={s.grad} style={S.statCard}>
              <MaterialCommunityIcons name={s.icon} size={22} color="#fff" style={{ marginBottom:10 }} />
              <Text style={{ fontSize:24, fontWeight:'800', color:'#fff' }}>{s.value}</Text>
              <Text style={{ fontSize:11, color:'rgba(255,255,255,0.85)', marginTop:4 }}>{s.label}</Text>
            </LinearGradient>
          ))}
        </View>

        {/* Phase selector */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:4 }} contentContainerStyle={{ paddingHorizontal:16, gap:8, paddingVertical:8 }}>
          {[{ id:'all', name:'All Phases', color:T.primary, icon:'apps-outline', gradient:[T.primary,'#2563eb'] }, ...GROWTH_PHASES].map(ph => (
            <TouchableOpacity key={ph.id} onPress={() => setActivePhase(ph.id)} style={{ borderRadius:30, overflow:'hidden', elevation:2 }}>
              <LinearGradient colors={activePhase===ph.id ? (ph.gradient||[T.primary,'#2563eb']) : ['#f1f5f9','#e2e8f0']} style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:9, gap:7 }}>
                <Ionicons name={ph.icon||'apps-outline'} size={15} color={activePhase===ph.id?'#fff':T.gray} />
                <Text style={{ fontSize:13, fontWeight:'700', color:activePhase===ph.id?'#fff':T.gray }}>{ph.name}</Text>
              </LinearGradient>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* ── METRICS GRID ───────────────────────────────────────────── */}
        <SectionHeader title="Performance Metrics" icon="chart-line" gradient={[T.primary,T.indigo]}
          action="View Forecast" onAction={() => setShowForecast(true)} />
        <View style={S.metricsGrid}>
          {[
            { icon:'trending-up',   label:'Annual Revenue', value:fmtMoney(metrics.revenue?.current),  target:fmtMoney(metrics.revenue?.target),  growth:metrics.revenue?.growth||0,   color:T.primary,  grad:[T.primary,'#2563eb']  },
            { icon:'users',         label:'Customers',      value:String(metrics.customers?.current||0),target:String(metrics.customers?.target||0),growth:metrics.customers?.growth||0, color:T.success,  grad:[T.success,'#059669']  },
            { icon:'repeat',        label:'MRR',            value:fmtMoney(metrics.mrr?.current),       target:fmtMoney(metrics.mrr?.target),       growth:metrics.mrr?.growth||0,       color:T.teal,     grad:[T.teal,'#0d9488']     },
            { icon:'pie-chart',     label:'Market Share',   value:`${metrics.marketShare||0}%`,         target:`${(metrics.marketShare||0)+5}%`,     growth:5,                            color:T.purple,   grad:[T.purple,'#7c3aed']   },
            { icon:'credit-card',        label:'CAC',            value:fmtMoney(metrics.cac?.current),       target:fmtMoney(metrics.cac?.target),        growth:metrics.cac?.growth||0,       color:T.orange,   grad:[T.orange,'#ea580c'],  inv:true },
            { icon:'award', label:'LTV:CAC',        value:`${metrics.ltv?.current&&metrics.cac?.current?(metrics.ltv.current/metrics.cac.current).toFixed(1):'—'}x`, target:'>10x', growth:12, color:T.pink, grad:[T.pink,'#db2777'] },
          ].map((m,i) => (
            <MetricCard key={i} {...m} />
          ))}
        </View>

        {/* ── FINANCIAL HEALTH ────────────────────────────────────────── */}
        <SectionHeader title="Financial Health" icon="cash" gradient={[T.success,T.teal]}
          action="Full Forecast" onAction={() => setShowForecast(true)} />
        <View style={S.finGrid}>
          {[
            { label:'Burn Rate',   value:fmtMoney(metrics.burnRate||0),  sub:'per month', icon:'fire',         color:T.danger  },
            { label:'Runway',      value:`${metrics.runway||0} mo`,       sub:'remaining', icon:'run-fast',     color:T.success },
            { label:'Gross Margin',value:`${metrics.grossMargin||71}%`,   sub:'of revenue',icon:'percent',      color:T.indigo  },
            { label:'NPS Score',   value:String(metrics.nps||68),         sub:'net promoter',icon:'emoticon-happy',color:T.teal  },
            { label:'Churn Rate',  value:`${metrics.churnRate||2.1}%`,    sub:'monthly',   icon:'trending-down',color:T.warning },
            { label:'LTV',         value:fmtMoney(metrics.ltv?.current||0),sub:'lifetime',  icon:'diamond',     color:T.pink    },
          ].map((f,i) => (
            <View key={i} style={S.finCard}>
              <View style={[S.finIcon, { backgroundColor:f.color+'18' }]}>
                <MaterialCommunityIcons name={f.icon} size={20} color={f.color} />
              </View>
              <Text style={S.finLabel}>{f.label}</Text>
              <Text style={[S.finValue, { color:f.color }]}>{f.value}</Text>
              <Text style={S.finSub}>{f.sub}</Text>
            </View>
          ))}
        </View>

        {/* ── AI Strategy CTA ─────────────────────────────────────────── */}
        <TouchableOpacity onPress={() => setShowAIAdvisor(true)} activeOpacity={0.9} style={{ marginHorizontal:16, marginBottom:16 }}>
          <LinearGradient colors={[T.claude, T.primary]} start={{x:0,y:0}} end={{x:1,y:0}} style={{ borderRadius:16, padding:16, flexDirection:'row', alignItems:'center', gap:14 }}>
            <Animated.View style={{ transform:[{scale:pulse}], width:44, height:44, borderRadius:22, backgroundColor:'rgba(255,255,255,0.2)', alignItems:'center', justifyContent:'center' }}>
              <MaterialCommunityIcons name="brain" size={26} color="#fff" />
            </Animated.View>
            <View style={{ flex:1 }}>
              <Text style={{ fontSize:15, fontWeight:'800', color:'#fff' }}>{aiProvider} · Strategy Advisor</Text>
              <Text style={{ fontSize:12, color:'rgba(255,255,255,0.8)' }}>Get AI insights on your scale-up plan</Text>
            </View>
            <Ionicons name="arrow-forward-circle" size={26} color="rgba(255,255,255,0.8)" />
          </LinearGradient>
        </TouchableOpacity>

        {/* ── OKR SUMMARY ─────────────────────────────────────────────── */}
        <SectionHeader title={`OKRs (${(okrs||[]).length})`} icon="target" gradient={[T.orange,T.warning]}
          action="Manage" onAction={() => setShowOKRs(true)} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          {(okrs||[]).slice(0,2).map(okr => {
            const p = Math.round(okr.key_results.reduce((s,kr)=>s+pct(kr.current,kr.target),0)/Math.max(1,okr.key_results.length));
            return (
              <TouchableOpacity key={okr.id} onPress={() => setShowOKRs(true)}
                style={{ backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:10, borderWidth:1, borderColor:T.border }}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:10 }}>
                  <View style={{ backgroundColor:T.orange+'18', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                    <Text style={{ fontSize:11, fontWeight:'700', color:T.orange }}>{okr.period}</Text>
                  </View>
                  <Text style={{ flex:1, fontSize:14, fontWeight:'700', color:T.dark }} numberOfLines={1}>{okr.objective}</Text>
                  <Text style={{ fontSize:13, fontWeight:'800', color:p>=70?T.success:p>=40?T.warning:T.danger }}>{p}%</Text>
                </View>
                <View style={{ height:6, backgroundColor:T.border, borderRadius:3, overflow:'hidden' }}>
                  <LinearGradient colors={p>=70?[T.success,T.teal]:p>=40?[T.warning,T.orange]:[T.danger,'#dc2626']} style={{ width:`${p}%`, height:'100%', borderRadius:3 }} />
                </View>
                <View style={{ flexDirection:'row', gap:6, marginTop:8 }}>
                  {okr.key_results.slice(0,3).map((kr,i) => (
                    <View key={i} style={{ flex:1, backgroundColor:T.bg, borderRadius:6, padding:6 }}>
                      <Text style={{ fontSize:9, color:T.gray, marginBottom:2 }} numberOfLines={1}>{kr.text}</Text>
                      <Text style={{ fontSize:11, fontWeight:'700', color:T.dark }}>{kr.current}/{kr.target}{kr.unit}</Text>
                    </View>
                  ))}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* ── MILESTONES ──────────────────────────────────────────────── */}
        <SectionHeader title={`Milestones (${completedMs}/${(milestones||[]).length})`} icon="flag-outline" gradient={[T.teal,T.success]}
          action="+ Add" onAction={() => setShowAddMilestone(true)} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          {filteredMs.length===0 ? (
            <EmptyState icon="flag-outline" title="No milestones" sub="Set your first growth milestone" action="Add Milestone" onAction={() => setShowAddMilestone(true)} />
          ) : filteredMs.slice(0,6).map(ms => {
            const ph = GROWTH_PHASES.find(p=>p.id===ms.phase)||GROWTH_PHASES[0];
            const dl = daysLeft(ms.target_date);
            const pri = PRIORITY_LEVELS.find(p=>p.id===ms.priority)||PRIORITY_LEVELS[2];
            return (
              <TouchableOpacity key={ms.id} onPress={() => { setSelectedMs(ms); setShowMilestoneDetail(true); }}
                style={[S.msItem, ms.completed && { opacity:0.65 }]}>
                <TouchableOpacity onPress={() => toggleMilestone(ms.id)} style={[S.msCk, { backgroundColor: ms.completed ? T.success : 'transparent', borderColor: ms.completed ? T.success : ph.color }]}>
                  {ms.completed && <Ionicons name="checkmark" size={14} color="#fff" />}
                </TouchableOpacity>
                <View style={{ flex:1 }}>
                  <Text style={[S.msTitle, ms.completed && { textDecorationLine:'line-through', color:T.gray }]} numberOfLines={1}>{ms.title}</Text>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:4 }}>
                    <View style={{ backgroundColor:ph.color+'20', borderRadius:5, paddingHorizontal:6, paddingVertical:2, flexDirection:'row', alignItems:'center', gap:4 }}>
                      <Ionicons name={ph.icon} size={10} color={ph.color} />
                      <Text style={{ fontSize:10, color:ph.color, fontWeight:'700' }}>{ph.name}</Text>
                    </View>
                    <View style={{ backgroundColor:pri.color+'15', borderRadius:5, paddingHorizontal:6, paddingVertical:2 }}>
                      <Text style={{ fontSize:10, color:pri.color, fontWeight:'700' }}>{pri.name}</Text>
                    </View>
                    <Text style={{ fontSize:10, color:dl<7?T.danger:T.gray, fontWeight:dl<7?'800':'500' }}>
                      <Text>{ms.completed ? '✓ Done' : dl>0 ? `${dl}d left` : 'Overdue'}</Text>
                    </Text>
                  </View>
                </View>
                <Ionicons name="chevron-forward" size={16} color={T.border} />
              </TouchableOpacity>
            );
          })}
        </View>

        {/* ── INITIATIVES ─────────────────────────────────────────────── */}
        <SectionHeader title={`Strategic Initiatives (${filteredInit.length})`} icon="rocket-outline" gradient={[T.primary,T.indigo]}
          action="+ New" onAction={() => setShowAddInitiative(true)} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          {filteredInit.length===0 ? (
            <EmptyState icon="rocket-outline" title="No initiatives" sub="Create your first strategic initiative" action="Create Initiative" onAction={() => setShowAddInitiative(true)} />
          ) : filteredInit.map(init => {
            const tp = INITIATIVE_TYPES.find(t=>t.id===init.type)||INITIATIVE_TYPES[0];
            const ph = GROWTH_PHASES.find(p=>p.id===init.phase)||GROWTH_PHASES[0];
            const pri = PRIORITY_LEVELS.find(p=>p.id===init.priority)||PRIORITY_LEVELS[2];
            return (
              <TouchableOpacity key={init.id} onPress={() => { setSelectedInit(init); setShowInitDetail(true); }}
                style={S.initCard}>
                <View style={{ height:4, backgroundColor:tp.color, borderTopLeftRadius:14, borderTopRightRadius:14 }} />
                <View style={{ padding:14 }}>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:10 }}>
                    <LinearGradient colors={tp.gradient} style={{ width:32, height:32, borderRadius:10, alignItems:'center', justifyContent:'center' }}>
                      <MaterialCommunityIcons name={tp.icon} size={17} color="#fff" />
                    </LinearGradient>
                    <View style={{ flex:1 }}>
                      <Text style={S.initTitle} numberOfLines={1}>{init.title}</Text>
                      <Text style={{ fontSize:11, color:tp.color, fontWeight:'700' }}>{tp.name}</Text>
                    </View>
                    <View style={{ alignItems:'flex-end', gap:4 }}>
                      <View style={{ backgroundColor:pri.color+'18', borderRadius:6, paddingHorizontal:7, paddingVertical:3 }}>
                        <Text style={{ fontSize:10, fontWeight:'800', color:pri.color }}>{String(pri.name ?? '').toUpperCase()}</Text>
                      </View>
                      <TouchableOpacity onPress={(e) => { e.stopPropagation(); deleteInitiative(init.id); }}>
                        <Ionicons name="trash-outline" size={15} color={T.danger} />
                      </TouchableOpacity>
                    </View>
                  </View>
                  <Text style={S.initDesc} numberOfLines={2}>{init.description}</Text>
                  <View style={{ flexDirection:'row', gap:8, marginBottom:10 }}>
                    <View style={{ backgroundColor:ph.color+'18', borderRadius:6, paddingHorizontal:8, paddingVertical:4, flexDirection:'row', alignItems:'center', gap:5 }}>
                      <Ionicons name={ph.icon} size={11} color={ph.color} />
                      <Text style={{ fontSize:11, fontWeight:'700', color:ph.color }}>{ph.name}</Text>
                    </View>
                    {init.budget>0 && (
                      <View style={{ backgroundColor:T.success+'15', borderRadius:6, paddingHorizontal:8, paddingVertical:4, flexDirection:'row', alignItems:'center', gap:4 }}>
                        <MaterialCommunityIcons name="cash" size={11} color={T.success} />
                        <Text style={{ fontSize:11, fontWeight:'700', color:T.success }}>{fmtMoney(init.budget)}</Text>
                      </View>
                    )}
                    <View style={{ backgroundColor:T.bg, borderRadius:6, paddingHorizontal:8, paddingVertical:4 }}>
                      <Text style={{ fontSize:11, color:T.gray }}>{init.owner||'Unassigned'}</Text>
                    </View>
                  </View>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:5 }}>
                    <Text style={{ fontSize:11, color:T.gray }}>Progress</Text>
                    <Text style={{ fontSize:11, fontWeight:'800', color:init.progress>=70?T.success:init.progress>=40?T.warning:T.primary }}>{init.progress||0}%</Text>
                  </View>
                  <View style={{ height:6, backgroundColor:T.border, borderRadius:3, overflow:'hidden' }}>
                    <LinearGradient colors={tp.gradient} style={{ width:`${init.progress||0}%`, height:'100%', borderRadius:3 }} />
                  </View>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* ── FUNDING TRACKER ─────────────────────────────────────────── */}
        <SectionHeader title="Funding Rounds" icon="bank" gradient={[T.success,T.teal]}
          action="+ Round" onAction={() => setShowFunding(true)} />
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }} contentContainerStyle={{ paddingHorizontal:16, gap:12 }}>
          {(funding||[]).map(f => (
            <View key={f.id} style={{ width:170, backgroundColor:'#fff', borderRadius:14, padding:14, borderWidth:1, borderColor:T.border }}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <Text style={{ fontSize:13, fontWeight:'800', color:T.dark }}>{f.stage}</Text>
                <View style={{ backgroundColor:f.status==='closed'?T.success+'18':T.warning+'18', borderRadius:6, paddingHorizontal:7, paddingVertical:3 }}>
                  <Text style={{ fontSize:10, fontWeight:'700', color:f.status==='closed'?T.success:T.warning }}>{f.status==='closed'?'Closed':'Raising'}</Text>
                </View>
              </View>
              <Text style={{ fontSize:22, fontWeight:'800', color:T.primary }}>{f.currency}{typeof f.amount==='number'?(f.amount>=1000000?(f.amount/1000000).toFixed(1)+'M':(f.amount/1000).toFixed(0)+'K'):f.amount}</Text>
              <Text style={{ fontSize:11, color:T.gray, marginTop:2 }}>Raised · {f.date}</Text>
              <View style={{ flexDirection:'row', justifyContent:'space-between', marginTop:10 }}>
                <View>
                  <Text style={{ fontSize:10, color:T.gray }}>Equity</Text>
                  <Text style={{ fontSize:13, fontWeight:'700', color:T.dark }}>{f.equity}%</Text>
                </View>
                {f.valuation > 0 && (
                  <View style={{ alignItems:'flex-end' }}>
                    <Text style={{ fontSize:10, color:T.gray }}>Valuation</Text>
                    <Text style={{ fontSize:13, fontWeight:'700', color:T.success }}>{fmtMoney(f.valuation)}</Text>
                  </View>
                )}
              </View>
            </View>
          ))}
          <TouchableOpacity onPress={() => setShowAddFunding(true)}
            style={{ width:120, backgroundColor:T.success+'12', borderRadius:14, padding:14, borderWidth:1.5, borderColor:T.success+'40', alignItems:'center', justifyContent:'center', gap:8 }}>
            <Ionicons name="add-circle-outline" size={28} color={T.success} />
            <Text style={{ fontSize:12, fontWeight:'700', color:T.success, textAlign:'center' }}>Add Round</Text>
          </TouchableOpacity>
        </ScrollView>

        {/* ── MARKETS ─────────────────────────────────────────────────── */}
        <SectionHeader title={`Markets (${(markets||[]).length})`} icon="map-marker-multiple" gradient={[T.orange,T.warning]}
          action="Manage" onAction={() => setShowMarkets(true)} />
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }} contentContainerStyle={{ paddingHorizontal:16, gap:12 }}>
          {(markets||[]).map(m => {
            const sc = m.status==='Active'?T.success:m.status==='Exploring'?T.warning:T.primary;
            return (
              <TouchableOpacity key={m.id} onPress={() => setShowMarkets(true)}
                style={{ width:150, backgroundColor:'#fff', borderRadius:14, padding:14, borderWidth:1, borderColor:T.border }}>
                <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <Text style={{ fontSize:13, fontWeight:'800', color:T.dark, flex:1 }}>{m.name}</Text>
                  <View style={{ backgroundColor:sc+'18', borderRadius:6, paddingHorizontal:6, paddingVertical:3 }}>
                    <Text style={{ fontSize:10, fontWeight:'700', color:sc }}>{m.status}</Text>
                  </View>
                </View>
                <Text style={{ fontSize:18, fontWeight:'800', color:T.primary, marginBottom:4 }}>{m.share}</Text>
                <Text style={{ fontSize:11, color:T.gray }}>Market Share</Text>
                <View style={{ flexDirection:'row', justifyContent:'space-between', marginTop:10 }}>
                  <View>
                    <Text style={{ fontSize:9, color:T.gray }}>Potential</Text>
                    <Text style={{ fontSize:11, fontWeight:'700', color:T.dark }}>{m.potential}</Text>
                  </View>
                  <View style={{ alignItems:'flex-end' }}>
                    <Text style={{ fontSize:9, color:T.gray }}>Competition</Text>
                    <Text style={{ fontSize:11, fontWeight:'700', color:T.dark }}>{m.competition}</Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* ── COMPETITOR INTEL ────────────────────────────────────────── */}
        <SectionHeader title={`Competitors (${(competitors||[]).length})`} icon="shield-half-full" gradient={[T.purple,T.pink]}
          action="Full Analysis" onAction={() => setShowCompetitors(true)} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          {(competitors||[]).slice(0,3).map(c => {
            const tc = c.threat==='High'?T.danger:c.threat==='Medium'?T.warning:T.success;
            return (
              <TouchableOpacity key={c.id} onPress={() => setShowCompetitors(true)} style={S.compCard}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:12, marginBottom:10 }}>
                  <View style={{ width:40, height:40, borderRadius:20, backgroundColor:T.primary+'18', alignItems:'center', justifyContent:'center' }}>
                    <Text style={{ fontSize:16, fontWeight:'800', color:T.primary }}>{c.name[0]}</Text>
                  </View>
                  <View style={{ flex:1 }}>
                    <Text style={{ fontSize:14, fontWeight:'800', color:T.dark }}>{c.name}</Text>
                    <Text style={{ fontSize:12, color:T.primary, fontWeight:'600' }}>{c.position}</Text>
                  </View>
                  <View style={{ backgroundColor:tc+'18', borderRadius:8, paddingHorizontal:8, paddingVertical:4 }}>
                    <Text style={{ fontSize:11, fontWeight:'800', color:tc }}>Threat: {c.threat}</Text>
                  </View>
                </View>
                {c.ourAdvantage ? (
                  <View style={{ backgroundColor:T.success+'12', borderRadius:8, padding:8 }}>
                    <Text style={{ fontSize:11, color:T.success, fontWeight:'700' }}>Our advantage: {c.ourAdvantage}</Text>
                  </View>
                ) : null}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* ── VISION CARD ─────────────────────────────────────────────── */}
        <TouchableOpacity onPress={() => setShowStrategy(true)} activeOpacity={0.9} style={{ marginHorizontal:16, marginBottom:16 }}>
          <LinearGradient colors={[T.primary+'12',T.secondary+'12']} style={{ borderRadius:18, padding:20, borderWidth:1, borderColor:T.primary+'30' }}>
            <View style={{ flexDirection:'row', alignItems:'center', gap:16 }}>
              <LinearGradient colors={[T.primary,T.indigo]} style={{ width:56, height:56, borderRadius:16, alignItems:'center', justifyContent:'center' }}>
                <MaterialCommunityIcons name="rocket-launch" size={28} color="#fff" />
              </LinearGradient>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:17, fontWeight:'800', color:T.dark, marginBottom:4 }}>Vision & Strategy</Text>
                <Text style={{ fontSize:13, color:T.gray, lineHeight:18 }} numberOfLines={2}>{strategy.vision}</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={T.primary} />
            </View>
          </LinearGradient>
        </TouchableOpacity>

        {/* ── TEAM ────────────────────────────────────────────────────── */}
        <SectionHeader title="Team Scaling" icon="account-group" gradient={[T.warning,T.orange]}
          action="Hiring Plan" onAction={() => setShowTeamPlan(true)} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          <View style={S.teamGrid}>
            {[
              { label:'Total Team',    value:(teamData?.total||0),         color:T.primary  },
              { label:'Engineering',   value:(teamData?.engineering||0),   color:T.indigo   },
              { label:'Sales',         value:(teamData?.sales||0),         color:T.success  },
              { label:'Product',       value:(teamData?.product||0)||0,    color:T.teal     },
              { label:'Operations',    value:(teamData?.operations||0)||0, color:T.orange   },
              { label:'Growth Target', value:`+${(teamData?.growthTarget||0)}%`, color:T.warning },
            ].map((t,i) => (
              <View key={i} style={S.teamCard}>
                <Text style={{ fontSize:22, fontWeight:'800', color:t.color }}>{t.value}</Text>
                <Text style={{ fontSize:11, color:T.gray, marginTop:4, textAlign:'center' }}>{t.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── TECH ROADMAP ────────────────────────────────────────────── */}
        <SectionHeader title="Technology Roadmap" icon="chip" gradient={[T.indigo,T.purple]} action={null} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          {(techRoadmap||[]).map((t,i) => {
            const sc = t.status==='active'?T.success:t.status==='planned'?T.primary:T.gray;
            return (
              <View key={i} style={{ flexDirection:'row', alignItems:'center', gap:12, paddingVertical:12, borderBottomWidth:i<(techRoadmap||[]).length-1?1:0, borderBottomColor:T.border }}>
                <View style={{ width:40, height:40, borderRadius:12, backgroundColor:T.indigo+'18', alignItems:'center', justifyContent:'center' }}>
                  <MaterialCommunityIcons name="chip" size={20} color={T.indigo} />
                </View>
                <View style={{ flex:1 }}>
                  <Text style={{ fontSize:14, fontWeight:'700', color:T.dark }}>{t.item}</Text>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:4 }}>
                    <Text style={{ fontSize:11, color:T.gray }}>{t.quarter}</Text>
                    <View style={{ backgroundColor:sc+'18', borderRadius:5, paddingHorizontal:6, paddingVertical:2 }}>
                      <Text style={{ fontSize:10, fontWeight:'700', color:sc }}>{t.status}</Text>
                    </View>
                    <Text style={{ fontSize:11, color:T.success, fontWeight:'600' }}>{t.impact}</Text>
                  </View>
                  {t.progress > 0 && (
                    <View style={{ height:4, backgroundColor:T.border, borderRadius:2, marginTop:6, overflow:'hidden' }}>
                      <LinearGradient colors={[T.indigo,T.purple]} style={{ width:`${t.progress}%`, height:'100%', borderRadius:2 }} />
                    </View>
                  )}
                </View>
                <Text style={{ fontSize:14, fontWeight:'800', color:T.indigo }}>{t.progress}%</Text>
              </View>
            );
          })}
        </View>

        {/* ── RISK REGISTER SUMMARY ───────────────────────────────────── */}
        <SectionHeader title={`Risk Register (${(risks||[]).length})`} icon="shield-alert" gradient={[T.danger,'#dc2626']}
          action="Manage" onAction={() => setShowRisks(true)} />
        <View style={{ paddingHorizontal:16, marginBottom:16 }}>
          <View style={{ flexDirection:'row', gap:10, marginBottom:12 }}>
            {RISK_LEVELS.map(l => {
              const cnt = (risks||[]).filter(r=>r.level===l&&r.status!=='mitigated').length;
              return (
                <View key={l} style={{ flex:1, backgroundColor:RISK_COLORS[l]+'18', borderRadius:10, padding:10, alignItems:'center', borderWidth:1, borderColor:RISK_COLORS[l]+'30' }}>
                  <Text style={{ fontSize:18, fontWeight:'800', color:RISK_COLORS[l] }}>{cnt}</Text>
                  <Text style={{ fontSize:10, color:T.gray, marginTop:3 }}>{l}</Text>
                </View>
              );
            })}
          </View>
          {(risks||[]).filter(r=>r.status!=='mitigated').slice(0,3).map(r => (
            <View key={r.id} style={{ flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#fff', borderRadius:10, padding:12, marginBottom:8, borderWidth:1, borderColor:T.border, borderLeftWidth:3, borderLeftColor:RISK_COLORS[r.level] }}>
              <MaterialCommunityIcons name="alert-circle" size={18} color={RISK_COLORS[r.level]} />
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:13, fontWeight:'700', color:T.dark }}>{r.title}</Text>
                <Text style={{ fontSize:11, color:T.gray }}>{r.category} · Score: {riskScore(r)}</Text>
              </View>
              <Text style={{ fontSize:11, fontWeight:'700', color:RISK_COLORS[r.level] }}>{r.level}</Text>
            </View>
          ))}
        </View>

        {/* ── QUICK ACTIONS ───────────────────────────────────────────── */}
        <View style={{ marginHorizontal:16, marginBottom:16, backgroundColor:'#fff', borderRadius:20, borderWidth:1, borderColor:T.border, padding:16 }}>
          <Text style={{ fontSize:15, fontWeight:'700', color:T.dark, marginBottom:14 }}>Quick Actions</Text>
          <View style={{ flexDirection:'row', flexWrap:'wrap', gap:12 }}>
            {[
              { icon:'chart-bar',        label:'Analytics',     grad:[T.primary,T.indigo], onPress:() => setShowAnalytics(true)   },
              { icon:'presentation',     label:'Investor Deck', grad:[T.success,T.teal],   onPress:() => { generateInvestorDeck(); setShowInvestorDeck(true); } },
              { icon:'shield-alert',     label:'Risk Register', grad:[T.danger,'#dc2626'], onPress:() => setShowRisks(true)       },
              { icon:'target',           label:'OKRs',          grad:[T.orange,T.warning],  onPress:() => setShowOKRs(true)        },
              { icon:'bank',             label:'Funding',       grad:[T.teal,T.success],   onPress:() => setShowFunding(true)     },
              { icon:'chart-timeline',   label:'Forecast',      grad:[T.indigo,T.purple],  onPress:() => setShowForecast(true)    },
              { icon:'account-group',    label:'Team Plan',     grad:[T.warning,T.orange], onPress:() => setShowTeamPlan(true)    },
              { icon:'map-marker-multiple',label:'Markets',     grad:[T.orange,'#ea580c'], onPress:() => setShowMarkets(true)     },
              { icon:'brain',            label:'AI Advisor',    grad:[T.claude,T.primary], onPress:() => setShowAIAdvisor(true)   },
              { icon:'share-variant',    label:'Share Report',  grad:[T.purple,T.pink],    onPress:() => shareText(`${companyName || 'Company'} Scale-Up Report\n\nARR: ${fmtMoney(metrics.revenue?.current)}\nCustomers: ${metrics.customers?.current}\nMRR: ${fmtMoney(metrics.mrr?.current)}\nGrowth: ${metrics.revenue?.growth}%\nRunway: ${metrics.runway} months\nTeam: ${(teamData?.total||0)}\n\nActive Initiatives: ${activeCount}\nMilestones Completed: ${completedMs}/${(milestones||[]).length}`, `${companyName || 'Company'} Scale-Up Report`) },
              { icon:'cog',              label:'Strategy',      grad:[T.dark,'#374151'],   onPress:() => setShowStrategy(true)    },
              { icon:'rocket-launch',    label:'ML Platform',   grad:['#667eea','#764ba2'],onPress:() => navigation?.navigate?.('MachineLearningScreen') },
            ].map((a,i) => (
              <TouchableOpacity key={i} onPress={a.onPress} style={{ width:(width-84)/4, alignItems:'center', gap:8 }}>
                <LinearGradient colors={a.grad} style={{ width:48, height:48, borderRadius:16, alignItems:'center', justifyContent:'center' }}>
                  <MaterialCommunityIcons name={a.icon} size={22} color="#fff" />
                </LinearGradient>
                <Text style={{ fontSize:11, fontWeight:'600', color:T.dark, textAlign:'center' }}>{a.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* cross-screen integration widgets */}
        <View style={{ flexDirection:'row', gap:12, marginHorizontal:16, marginBottom:16 }}>
          <TouchableOpacity onPress={() => navigation?.navigate?.('InnovativeScreen')} style={{ flex:1 }}>
            <LinearGradient colors={[T.claude+'20',T.primary+'20']} style={{ borderRadius:14, padding:14, borderWidth:1, borderColor:T.claude+'40' }}>
              <MaterialCommunityIcons name="brain" size={22} color={T.claude} />
              <Text style={{ fontSize:13, fontWeight:'800', color:T.dark, marginTop:8 }}>Innovation Hub</Text>
              <Text style={{ fontSize:11, color:T.gray, marginTop:2 }}>AI ideas & pipeline</Text>
            </LinearGradient>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => navigation?.navigate?.('MachineLearningScreen')} style={{ flex:1 }}>
            <LinearGradient colors={['#667eea20','#764ba220']} style={{ borderRadius:14, padding:14, borderWidth:1, borderColor:'#667eea40' }}>
              <MaterialCommunityIcons name="chart-line" size={22} color="#667eea" />
              <Text style={{ fontSize:13, fontWeight:'800', color:T.dark, marginTop:8 }}>ML Platform</Text>
              <Text style={{ fontSize:11, color:T.gray, marginTop:2 }}>Models & predictions</Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>

        <View style={{ height:100 }} />
      </Animated.ScrollView>

      {/* FAB */}
      <TouchableOpacity style={S.fab} onPress={() => setShowAddInitiative(true)} activeOpacity={0.9}>
        <LinearGradient colors={[T.primary,T.indigo]} style={S.fabGrad}>
          <Ionicons name="rocket" size={28} color="#fff" />
        </LinearGradient>
      </TouchableOpacity>

      {/* ──────────────────────────────────────────────────────────────────
          MODALS
      ────────────────────────────────────────────────────────────────── */}

      {/* ADD INITIATIVE */}
      <AddInitiativeModal visible={showAddInitiative} onClose={() => setShowAddInitiative(false)}
        form={initForm} setForm={setInitForm} onSubmit={createInitiative} />

      {/* ADD MILESTONE */}
      <AddMilestoneModal visible={showAddMilestone} onClose={() => setShowAddMilestone(false)}
        form={msForm} setForm={setMsForm} onSubmit={createMilestone} />

      {/* STRATEGY DETAIL */}
      <StrategyModal visible={showStrategy} onClose={() => setShowStrategy(false)} strategy={strategy} />

      {/* INITIATIVE DETAIL */}
      {selectedInit && (
        <Modal visible={showInitDetail} transparent animationType="slide" onRequestClose={() => setShowInitDetail(false)}>
          <View style={S.modalOverlay}>
            <View style={[S.modalSheet, { maxHeight:height*0.9 }]}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:20, borderBottomWidth:1, borderBottomColor:T.border }}>
                <View style={{ flex:1 }}>
                  <Text style={{ fontSize:18, fontWeight:'800', color:T.dark }}>{selectedInit.title}</Text>
                  <Text style={{ fontSize:12, color:T.primary, marginTop:2 }}>{INITIATIVE_TYPES.find(t=>t.id===selectedInit.type)?.name}</Text>
                </View>
                <TouchableOpacity onPress={() => setShowInitDetail(false)} style={{ padding:4 }}>
                  <Ionicons name="close" size={24} color={T.gray} />
                </TouchableOpacity>
              </View>
              <ScrollView style={{ padding:20 }}>
                <DetailRow label="Phase"          value={GROWTH_PHASES.find(p=>p.id===selectedInit.phase)?.name||'—'} />
                <DetailRow label="Priority"       value={selectedInit.priority} />
                <DetailRow label="Owner"          value={selectedInit.owner||'Unassigned'} />
                <DetailRow label="Budget"         value={fmtMoney(selectedInit.budget)} />
                <DetailRow label="Start"          value={fmtDate(selectedInit.start_date)} />
                <DetailRow label="End"            value={fmtDate(selectedInit.end_date)} />
                <DetailRow label="Status"         value={selectedInit.status} />
                {selectedInit.description ? <View style={{ marginBottom:16 }}><Text style={{ fontSize:12, fontWeight:'700', color:T.gray, marginBottom:4 }}>DESCRIPTION</Text><Text style={{ fontSize:14, color:T.dark, lineHeight:22 }}>{selectedInit.description}</Text></View> : null}
                {selectedInit.success_metrics ? <View style={{ marginBottom:16 }}><Text style={{ fontSize:12, fontWeight:'700', color:T.gray, marginBottom:4 }}>SUCCESS METRICS</Text><Text style={{ fontSize:14, color:T.dark, lineHeight:22 }}>{selectedInit.success_metrics}</Text></View> : null}
                {selectedInit.risks ? <View style={{ marginBottom:16 }}><Text style={{ fontSize:12, fontWeight:'700', color:T.danger, marginBottom:4 }}>RISKS</Text><Text style={{ fontSize:14, color:T.dark, lineHeight:22 }}>{selectedInit.risks}</Text></View> : null}
                <Text style={{ fontSize:12, fontWeight:'700', color:T.gray, marginBottom:8 }}>PROGRESS: {selectedInit.progress||0}%</Text>
                <View style={{ height:8, backgroundColor:T.border, borderRadius:4, overflow:'hidden', marginBottom:16 }}>
                  <LinearGradient colors={[T.primary,T.indigo]} style={{ width:`${selectedInit.progress||0}%`, height:'100%', borderRadius:4 }} />
                </View>
                <TouchableOpacity onPress={() => { setShowInitDetail(false); setShowAIAdvisor(true); setAiPrompt(`Analyse this strategic initiative and suggest improvements:\nTitle: ${selectedInit.title}\nDescription: ${selectedInit.description}\nSuccess metrics: ${selectedInit.success_metrics}\nCurrent progress: ${selectedInit.progress}%\nRisks: ${selectedInit.risks}`); }}
                  style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:T.claude+'18', borderRadius:12, padding:14, marginBottom:20, borderWidth:1, borderColor:T.claude+'40' }}>
                  <MaterialCommunityIcons name="brain" size={18} color={T.claude} />
                  <Text style={{ fontSize:14, fontWeight:'700', color:T.claude }}>AI: Analyse This Initiative</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      {/* MILESTONE DETAIL */}
      {selectedMs && (
        <Modal visible={showMilestoneDetail} transparent animationType="slide" onRequestClose={() => setShowMilestoneDetail(false)}>
          <View style={S.modalOverlay}>
            <View style={[S.modalSheet, { maxHeight:height*0.6 }]}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:20, borderBottomWidth:1, borderBottomColor:T.border }}>
                <Text style={{ fontSize:18, fontWeight:'800', color:T.dark, flex:1 }}>{selectedMs.title}</Text>
                <TouchableOpacity onPress={() => setShowMilestoneDetail(false)}><Ionicons name="close" size={24} color={T.gray} /></TouchableOpacity>
              </View>
              <ScrollView style={{ padding:20 }}>
                <DetailRow label="Phase"    value={GROWTH_PHASES.find(p=>p.id===selectedMs.phase)?.name||'—'} />
                <DetailRow label="Due"      value={fmtDate(selectedMs.target_date)} />
                <DetailRow label="Priority" value={selectedMs.priority||'medium'} />
                <DetailRow label="Status"   value={selectedMs.completed?'Completed':'Pending'} />
                {selectedMs.description ? <View style={{ marginBottom:16 }}><Text style={{ fontSize:12, fontWeight:'700', color:T.gray, marginBottom:4 }}>DESCRIPTION</Text><Text style={{ fontSize:14, color:T.dark, lineHeight:22 }}>{selectedMs.description}</Text></View> : null}
                {selectedMs.success_criteria ? <View style={{ marginBottom:16 }}><Text style={{ fontSize:12, fontWeight:'700', color:T.gray, marginBottom:4 }}>SUCCESS CRITERIA</Text><Text style={{ fontSize:14, color:T.dark, lineHeight:22 }}>{selectedMs.success_criteria}</Text></View> : null}
                <TouchableOpacity onPress={() => { toggleMilestone(selectedMs.id); setShowMilestoneDetail(false); }}
                  style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:selectedMs.completed?T.warning+'18':T.success+'18', borderRadius:12, padding:14 }}>
                  <Ionicons name={selectedMs.completed?'refresh-circle':'checkmark-circle'} size={20} color={selectedMs.completed?T.warning:T.success} />
                  <Text style={{ fontSize:14, fontWeight:'700', color:selectedMs.completed?T.warning:T.success }}>{selectedMs.completed?'Mark as Pending':'Mark as Complete'}</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      {/* ANALYTICS */}
      <Modal visible={showAnalytics} transparent animationType="slide" onRequestClose={() => setShowAnalytics(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="Analytics Dashboard" icon="chart-bar" gradient={[T.primary,T.indigo]} onClose={() => setShowAnalytics(false)} />
            <ScrollView style={{ padding:20 }}>
              <Text style={S.modalSectionTitle}>Revenue Performance</Text>
              <BarChart data={[
                { label:'Jan', value:85 }, { label:'Feb', value:91 }, { label:'Mar', value:97 },
                { label:'Apr', value:103 },{ label:'May', value:110 },{ label:'Jun', value:118 },
              ]} color={T.primary} />
              <Text style={[S.modalSectionTitle, { marginTop:20 }]}>Customer Growth</Text>
              <BarChart data={[
                { label:'Jan', value:210 }, { label:'Feb', value:228 }, { label:'Mar', value:248 },
                { label:'Apr', value:261 }, { label:'May', value:275 }, { label:'Jun', value:287 },
              ]} color={T.success} />
              <Text style={[S.modalSectionTitle, { marginTop:20 }]}>Initiative Progress Distribution</Text>
              <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
                {[
                  { label:'Planning', val:(initiatives||[]).filter(i=>i.status==='planning').length, color:T.gray   },
                  { label:'Active',   val:(initiatives||[]).filter(i=>i.status==='active').length,   color:T.success },
                  { label:'Paused',   val:(initiatives||[]).filter(i=>i.status==='paused').length,   color:T.warning },
                  { label:'Done',     val:(initiatives||[]).filter(i=>i.status==='completed').length,color:T.primary },
                ].map((s,i) => (
                  <View key={i} style={{ flex:1, backgroundColor:s.color+'18', borderRadius:10, padding:10, alignItems:'center' }}>
                    <Text style={{ fontSize:22, fontWeight:'800', color:s.color }}>{s.val}</Text>
                    <Text style={{ fontSize:10, color:T.gray, marginTop:4 }}>{s.label}</Text>
                  </View>
                ))}
              </View>
              <Text style={[S.modalSectionTitle, { marginTop:4 }]}>Budget by Initiative Type</Text>
              {INITIATIVE_TYPES.map(tp => {
                const total = (initiatives||[]).filter(i=>i.type===tp.id).reduce((s,i)=>s+(i.budget||0),0);
                const maxB  = Math.max(...INITIATIVE_TYPES.map(t => (initiatives||[]).filter(i=>i.type===t.id).reduce((s,i)=>s+(i.budget||0),0)), 1);
                return total > 0 ? (
                  <View key={tp.id} style={{ marginBottom:10 }}>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                      <Text style={{ fontSize:12, color:T.dark, fontWeight:'600' }}>{tp.name}</Text>
                      <Text style={{ fontSize:12, fontWeight:'800', color:tp.color }}>{fmtMoney(total)}</Text>
                    </View>
                    <View style={{ height:8, backgroundColor:T.border, borderRadius:4, overflow:'hidden' }}>
                      <LinearGradient colors={tp.gradient} style={{ width:`${(total/maxB)*100}%`, height:'100%', borderRadius:4 }} />
                    </View>
                  </View>
                ) : null;
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* RISK REGISTER */}
      <Modal visible={showRisks} transparent animationType="slide" onRequestClose={() => setShowRisks(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="Risk Register" icon="shield-alert" gradient={[T.danger,'#dc2626']} onClose={() => setShowRisks(false)}
              action={<TouchableOpacity onPress={() => { setShowRisks(false); setShowAddRisk(true); }} style={{ backgroundColor:T.danger+'20', borderRadius:10, paddingHorizontal:12, paddingVertical:7 }}><Text style={{ fontSize:13, fontWeight:'700', color:T.danger }}>+ Add Risk</Text></TouchableOpacity>} />
            <ScrollView style={{ padding:20 }}>
              <View style={{ flexDirection:'row', gap:8, marginBottom:16 }}>
                {RISK_LEVELS.map(l => {
                  const cnt = (risks||[]).filter(r=>r.level===l).length;
                  return (
                    <View key={l} style={{ flex:1, backgroundColor:RISK_COLORS[l]+'18', borderRadius:10, padding:10, alignItems:'center' }}>
                      <Text style={{ fontSize:20, fontWeight:'800', color:RISK_COLORS[l] }}>{cnt}</Text>
                      <Text style={{ fontSize:10, color:T.gray, marginTop:3 }}>{l}</Text>
                    </View>
                  );
                })}
              </View>
              {(risks||[]).map(r => (
                <View key={r.id} style={{ backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:10, borderWidth:1, borderColor:T.border, borderLeftWidth:4, borderLeftColor:RISK_COLORS[r.level] }}>
                  <View style={{ flexDirection:'row', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:14, fontWeight:'800', color:T.dark }}>{r.title}</Text>
                      <Text style={{ fontSize:12, color:T.gray, marginTop:2 }}>{r.category} · Owner: {r.owner}</Text>
                    </View>
                    <View style={{ alignItems:'flex-end', gap:4 }}>
                      <View style={{ backgroundColor:RISK_COLORS[r.level]+'18', borderRadius:6, paddingHorizontal:8, paddingVertical:4 }}>
                        <Text style={{ fontSize:11, fontWeight:'800', color:RISK_COLORS[r.level] }}>{r.level}</Text>
                      </View>
                      {r.status==='mitigated' && <View style={{ backgroundColor:T.success+'18', borderRadius:6, paddingHorizontal:8, paddingVertical:4 }}><Text style={{ fontSize:11, fontWeight:'700', color:T.success }}>Mitigated</Text></View>}
                    </View>
                  </View>
                  <View style={{ flexDirection:'row', gap:12, marginBottom:8 }}>
                    <View style={{ flex:1, backgroundColor:T.bg, borderRadius:8, padding:8 }}>
                      <Text style={{ fontSize:10, color:T.gray }}>Probability</Text>
                      <Text style={{ fontSize:16, fontWeight:'800', color:T.dark }}>{r.probability}%</Text>
                    </View>
                    <View style={{ flex:1, backgroundColor:T.bg, borderRadius:8, padding:8 }}>
                      <Text style={{ fontSize:10, color:T.gray }}>Impact</Text>
                      <Text style={{ fontSize:16, fontWeight:'800', color:T.dark }}>{r.impact}%</Text>
                    </View>
                    <View style={{ flex:1, backgroundColor:T.bg, borderRadius:8, padding:8 }}>
                      <Text style={{ fontSize:10, color:T.gray }}>Risk Score</Text>
                      <Text style={{ fontSize:16, fontWeight:'800', color:RISK_COLORS[r.level] }}>{riskScore(r)}</Text>
                    </View>
                  </View>
                  {r.mitigation ? (
                    <View style={{ backgroundColor:T.success+'12', borderRadius:8, padding:10 }}>
                      <Text style={{ fontSize:11, fontWeight:'700', color:T.success, marginBottom:3 }}>Mitigation</Text>
                      <Text style={{ fontSize:12, color:T.dark }}>{r.mitigation}</Text>
                    </View>
                  ) : null}
                  <View style={{ flexDirection:'row', gap:8, marginTop:10 }}>
                    <TouchableOpacity onPress={() => setRisks(p => p.map(x => x.id===r.id ? {...x, status:x.status==='mitigated'?'active':'mitigated'} : x))}
                      style={{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, paddingVertical:8, borderRadius:8, backgroundColor:r.status==='mitigated'?T.warning+'18':T.success+'18', borderWidth:1, borderColor:r.status==='mitigated'?T.warning+'40':T.success+'40' }}>
                      <Ionicons name={r.status==='mitigated'?'refresh-circle':'checkmark-circle'} size={16} color={r.status==='mitigated'?T.warning:T.success} />
                      <Text style={{ fontSize:12, fontWeight:'700', color:r.status==='mitigated'?T.warning:T.success }}>{r.status==='mitigated'?'Reopen':'Mitigate'}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => deleteRisk(r.id)} style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:8, backgroundColor:T.danger+'15', borderWidth:1, borderColor:T.danger+'30' }}>
                      <Ionicons name="trash-outline" size={16} color={T.danger} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ADD RISK */}
      <Modal visible={showAddRisk} transparent animationType="slide" onRequestClose={() => setShowAddRisk(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.85 }]}>
            <ModalHeader title="Add Risk" icon="shield-alert" gradient={[T.danger,'#dc2626']} onClose={() => setShowAddRisk(false)} />
            <ScrollView style={{ padding:20 }}>
              <TextInput placeholder="Risk title *" value={riskForm.title} onChangeText={v=>setRiskForm({...riskForm,title:v})} style={S.input} placeholderTextColor="#94a3b8" />
              <Text style={S.label}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
                {RISK_CATS.map(c => (
                  <TouchableOpacity key={c} onPress={() => setRiskForm({...riskForm,category:c})} style={{ marginRight:8 }}>
                    <View style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, backgroundColor:riskForm.category===c?T.danger+'20':T.bg, borderWidth:1, borderColor:riskForm.category===c?T.danger:T.border }}>
                      <Text style={{ fontSize:13, fontWeight:'700', color:riskForm.category===c?T.danger:T.gray }}>{c}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={S.label}>Severity Level</Text>
              <View style={{ flexDirection:'row', gap:8, marginBottom:16 }}>
                {RISK_LEVELS.map(l => (
                  <TouchableOpacity key={l} onPress={() => setRiskForm({...riskForm,level:l})} style={{ flex:1, paddingVertical:10, borderRadius:10, backgroundColor:riskForm.level===l?RISK_COLORS[l]:T.bg, borderWidth:1, borderColor:riskForm.level===l?RISK_COLORS[l]:T.border, alignItems:'center' }}>
                    <Text style={{ fontSize:12, fontWeight:'800', color:riskForm.level===l?'#fff':T.gray }}>{l}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <TextInput placeholder="Owner" value={riskForm.owner} onChangeText={v=>setRiskForm({...riskForm,owner:v})} style={S.input} placeholderTextColor="#94a3b8" />
              <TextInput placeholder="Mitigation strategy" value={riskForm.mitigation} onChangeText={v=>setRiskForm({...riskForm,mitigation:v})} multiline numberOfLines={3} style={[S.input,{minHeight:80,textAlignVertical:'top'}]} placeholderTextColor="#94a3b8" />
              <TouchableOpacity onPress={createRisk} style={{ borderRadius:14, overflow:'hidden', marginBottom:20 }}>
                <LinearGradient colors={[T.danger,'#dc2626']} style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:16, gap:8 }}>
                  <Ionicons name="shield-checkmark" size={20} color="#fff" />
                  <Text style={{ fontSize:16, fontWeight:'700', color:'#fff' }}>Add to Risk Register</Text>
                </LinearGradient>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* OKRs */}
      <Modal visible={showOKRs} transparent animationType="slide" onRequestClose={() => setShowOKRs(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="OKRs" icon="target" gradient={[T.orange,T.warning]} onClose={() => setShowOKRs(false)}
              action={<TouchableOpacity onPress={() => { setShowOKRs(false); setShowAddOKR(true); }} style={{ backgroundColor:T.orange+'20', borderRadius:10, paddingHorizontal:12, paddingVertical:7 }}><Text style={{ fontSize:13, fontWeight:'700', color:T.orange }}>+ OKR</Text></TouchableOpacity>} />
            <ScrollView style={{ padding:20 }}>
              {(okrs||[]).map(okr => {
                const p = Math.round(okr.key_results.reduce((s,kr)=>s+pct(kr.current,kr.target),0)/Math.max(1,okr.key_results.length));
                return (
                  <View key={okr.id} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:14, borderWidth:1, borderColor:T.border }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:12 }}>
                      <View style={{ backgroundColor:T.orange+'18', borderRadius:8, paddingHorizontal:10, paddingVertical:5 }}>
                        <Text style={{ fontSize:12, fontWeight:'800', color:T.orange }}>{okr.period}</Text>
                      </View>
                      <Text style={{ flex:1, fontSize:15, fontWeight:'800', color:T.dark }}>{okr.objective}</Text>
                      <Text style={{ fontSize:17, fontWeight:'800', color:p>=70?T.success:p>=40?T.warning:T.danger }}>{p}%</Text>
                    </View>
                    {okr.key_results.map((kr,i) => (
                      <View key={i} style={{ marginBottom:10 }}>
                        <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:5 }}>
                          <Text style={{ fontSize:13, color:T.dark, flex:1 }}>{kr.text}</Text>
                          <Text style={{ fontSize:13, fontWeight:'800', color:T.primary }}>{kr.current}/{kr.target} {kr.unit}</Text>
                        </View>
                        <View style={{ height:6, backgroundColor:T.border, borderRadius:3, overflow:'hidden' }}>
                          <LinearGradient colors={[T.orange,T.warning]} style={{ width:`${pct(kr.current,kr.target)}%`, height:'100%', borderRadius:3 }} />
                        </View>
                      </View>
                    ))}
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ADD OKR */}
      <Modal visible={showAddOKR} transparent animationType="slide" onRequestClose={() => setShowAddOKR(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.8 }]}>
            <ModalHeader title="Add OKR" icon="target" gradient={[T.orange,T.warning]} onClose={() => setShowAddOKR(false)} />
            <ScrollView style={{ padding:20 }}>
              <Text style={S.label}>Period</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
                {OKR_PERIODS.map(p => (
                  <TouchableOpacity key={p} onPress={() => setOkrForm({...okrForm,period:p})} style={{ marginRight:8 }}>
                    <View style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, backgroundColor:okrForm.period===p?T.orange+'20':T.bg, borderWidth:1, borderColor:okrForm.period===p?T.orange:T.border }}>
                      <Text style={{ fontSize:13, fontWeight:'700', color:okrForm.period===p?T.orange:T.gray }}>{p}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <TextInput placeholder="Objective *" value={okrForm.objective} onChangeText={v=>setOkrForm({...okrForm,objective:v})} style={S.input} placeholderTextColor="#94a3b8" />
              <Text style={S.label}>Key Results</Text>
              {okrForm.key_results.map((kr,i) => (
                <View key={i} style={{ backgroundColor:T.bg, borderRadius:12, padding:12, marginBottom:10 }}>
                  <TextInput placeholder={`Key Result ${i+1}`} value={kr.text} onChangeText={v=>{const krs=[...okrForm.key_results];krs[i]={...krs[i],text:v};setOkrForm({...okrForm,key_results:krs});}} style={[S.input,{marginBottom:8,backgroundColor:'#fff'}]} placeholderTextColor="#94a3b8" />
                  <View style={{ flexDirection:'row', gap:8 }}>
                    <TextInput placeholder="Current" value={String(kr.current)} onChangeText={v=>{const krs=[...okrForm.key_results];krs[i]={...krs[i],current:+v||0};setOkrForm({...okrForm,key_results:krs});}} keyboardType="numeric" style={[S.input,{flex:1,marginBottom:0,backgroundColor:'#fff'}]} placeholderTextColor="#94a3b8" />
                    <TextInput placeholder="Target" value={String(kr.target)} onChangeText={v=>{const krs=[...okrForm.key_results];krs[i]={...krs[i],target:+v||100};setOkrForm({...okrForm,key_results:krs});}} keyboardType="numeric" style={[S.input,{flex:1,marginBottom:0,backgroundColor:'#fff'}]} placeholderTextColor="#94a3b8" />
                    <TextInput placeholder="Unit" value={kr.unit} onChangeText={v=>{const krs=[...okrForm.key_results];krs[i]={...krs[i],unit:v};setOkrForm({...okrForm,key_results:krs});}} style={[S.input,{flex:1,marginBottom:0,backgroundColor:'#fff'}]} placeholderTextColor="#94a3b8" />
                  </View>
                </View>
              ))}
              <TouchableOpacity onPress={()=>setOkrForm({...okrForm,key_results:[...okrForm.key_results,{text:'',current:0,target:100,unit:'%'}]})} style={{ alignItems:'center', paddingVertical:10, marginBottom:16 }}>
                <Text style={{ fontSize:14, color:T.primary, fontWeight:'700' }}>+ Add Key Result</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={createOKR} style={{ borderRadius:14, overflow:'hidden', marginBottom:20 }}>
                <LinearGradient colors={[T.orange,T.warning]} style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:16, gap:8 }}>
                  <Ionicons name="target" size={20} color="#fff" />
                  <Text style={{ fontSize:16, fontWeight:'700', color:'#fff' }}>Create OKR</Text>
                </LinearGradient>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* INVESTOR DECK */}
      <Modal visible={showInvestorDeck} transparent animationType="slide" onRequestClose={() => setShowInvestorDeck(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="Investor Deck" icon="presentation" gradient={[T.success,T.teal]} onClose={() => setShowInvestorDeck(false)}
              action={
                <TouchableOpacity onPress={() => shareText(deckContent||'Generating…', `${companyName || 'Company'} Investor Deck`)} style={{ backgroundColor:T.success+'20', borderRadius:10, paddingHorizontal:12, paddingVertical:7 }}>
                  <Text style={{ fontSize:13, fontWeight:'700', color:T.success }}>Share</Text>
                </TouchableOpacity>
              } />
            <ScrollView style={{ padding:20 }}>
              {deckLoading ? (
                <View style={{ alignItems:'center', paddingVertical:60 }}>
                  <ActivityIndicator size="large" color={T.success} />
                  <Text style={{ fontSize:15, color:T.gray, marginTop:20 }}>Generating investor deck…</Text>
                  <Text style={{ fontSize:13, color:T.gray, marginTop:8 }}>Using {aiProvider}</Text>
                </View>
              ) : (
                <View style={{ backgroundColor:T.bg, borderRadius:14, padding:16 }}>
                  <Text style={{ fontSize:14, color:T.dark, lineHeight:22, fontFamily:Platform.OS==='ios'?'Courier':'monospace' }}>{deckContent}</Text>
                </View>
              )}
              {!deckLoading && deckContent && (
                <View style={{ flexDirection:'row', gap:10, marginTop:16, marginBottom:20 }}>
                  <TouchableOpacity onPress={generateInvestorDeck} style={{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, paddingVertical:12, borderRadius:12, borderWidth:1.5, borderColor:T.success }}>
                    <Ionicons name="refresh" size={16} color={T.success} />
                    <Text style={{ fontSize:13, fontWeight:'700', color:T.success }}>Regenerate</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => shareText(deckContent, `${companyName || 'Company'} Investor Deck`)} style={{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, paddingVertical:12, borderRadius:12, backgroundColor:T.success }}>
                    <Ionicons name="share-outline" size={16} color="#fff" />
                    <Text style={{ fontSize:13, fontWeight:'700', color:'#fff' }}>Share Deck</Text>
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* FUNDING */}
      <Modal visible={showFunding} transparent animationType="slide" onRequestClose={() => setShowFunding(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.85 }]}>
            <ModalHeader title="Funding Rounds" icon="bank" gradient={[T.success,T.teal]} onClose={() => setShowFunding(false)}
              action={<TouchableOpacity onPress={() => { setShowFunding(false); setShowAddFunding(true); }} style={{ backgroundColor:T.success+'20', borderRadius:10, paddingHorizontal:12, paddingVertical:7 }}><Text style={{ fontSize:13, fontWeight:'700', color:T.success }}>+ Round</Text></TouchableOpacity>} />
            <ScrollView style={{ padding:20 }}>
              <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
                {[
                  { label:'Total Raised', value:fmtMoney(funding.reduce((s,f)=>s+(f.status==='closed'?f.amount||0:0),0)), color:T.success },
                  { label:'Total Diluted', value:`${funding.reduce((s,f)=>s+(f.equity||0),0)}%`, color:T.danger },
                  { label:'Latest Valuation', value:fmtMoney(funding[(funding||[]).length-1]?.valuation||0), color:T.primary },
                ].map((s,i) => (
                  <View key={i} style={{ flex:1, backgroundColor:s.color+'15', borderRadius:10, padding:10, alignItems:'center' }}>
                    <Text style={{ fontSize:15, fontWeight:'800', color:s.color }}>{s.value}</Text>
                    <Text style={{ fontSize:10, color:T.gray, marginTop:3, textAlign:'center' }}>{s.label}</Text>
                  </View>
                ))}
              </View>
              {(funding||[]).map(f => (
                <View key={f.id} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:12, borderWidth:1, borderColor:T.border }}>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <Text style={{ fontSize:17, fontWeight:'800', color:T.dark }}>{f.stage}</Text>
                    <View style={{ backgroundColor:f.status==='closed'?T.success+'18':T.warning+'18', borderRadius:8, paddingHorizontal:10, paddingVertical:5 }}>
                      <Text style={{ fontSize:12, fontWeight:'700', color:f.status==='closed'?T.success:T.warning }}>{f.status==='closed'?'Closed':'Raising'}</Text>
                    </View>
                  </View>
                  <View style={{ flexDirection:'row', gap:12 }}>
                    {[
                      { l:'Amount',    v:fmtMoney(f.amount)        },
                      { l:'Equity',    v:`${f.equity}%`            },
                      { l:'Valuation', v:fmtMoney(f.valuation)     },
                      { l:'Date',      v:f.date                     },
                    ].map((d,i) => (
                      <View key={i} style={{ flex:1 }}>
                        <Text style={{ fontSize:10, color:T.gray }}>{d.l}</Text>
                        <Text style={{ fontSize:13, fontWeight:'700', color:T.dark }}>{d.v}</Text>
                      </View>
                    ))}
                  </View>
                  {f.investors?.length>0 && (
                    <Text style={{ fontSize:12, color:T.gray, marginTop:8 }}>Investors: {Array.isArray(f.investors)?f.investors.join(', '):f.investors}</Text>
                  )}
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ADD FUNDING */}
      <Modal visible={showAddFunding} transparent animationType="slide" onRequestClose={() => setShowAddFunding(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.85 }]}>
            <ModalHeader title="Add Funding Round" icon="bank" gradient={[T.success,T.teal]} onClose={() => setShowAddFunding(false)} />
            <ScrollView style={{ padding:20 }}>
              <Text style={S.label}>Stage</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
                {FUNDING_STAGES.map(s => (
                  <TouchableOpacity key={s} onPress={() => setFundingForm({...fundingForm,stage:s})} style={{ marginRight:8 }}>
                    <View style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, backgroundColor:fundingForm.stage===s?T.success+'20':T.bg, borderWidth:1, borderColor:fundingForm.stage===s?T.success:T.border }}>
                      <Text style={{ fontSize:13, fontWeight:'700', color:fundingForm.stage===s?T.success:T.gray }}>{s}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <TextInput placeholder="Amount (£ number)" value={String(fundingForm.amount)} onChangeText={v=>setFundingForm({...fundingForm,amount:v.replace(/[^0-9]/g,'')})} keyboardType="numeric" style={S.input} placeholderTextColor="#94a3b8" />
              <TextInput placeholder="Date (e.g. Q2 2025)" value={fundingForm.date} onChangeText={v=>setFundingForm({...fundingForm,date:v})} style={S.input} placeholderTextColor="#94a3b8" />
              <TextInput placeholder="Investors" value={fundingForm.investors} onChangeText={v=>setFundingForm({...fundingForm,investors:v})} style={S.input} placeholderTextColor="#94a3b8" />
              <TextInput placeholder="Equity %" value={String(fundingForm.equity)} onChangeText={v=>setFundingForm({...fundingForm,equity:v})} keyboardType="numeric" style={S.input} placeholderTextColor="#94a3b8" />
              <TextInput placeholder="Pre-money valuation (£)" value={String(fundingForm.valuation)} onChangeText={v=>setFundingForm({...fundingForm,valuation:v})} keyboardType="numeric" style={[S.input,{marginBottom:24}]} placeholderTextColor="#94a3b8" />
              <TouchableOpacity onPress={createFunding} style={{ borderRadius:14, overflow:'hidden', marginBottom:20 }}>
                <LinearGradient colors={[T.success,T.teal]} style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:16, gap:8 }}>
                  <MaterialCommunityIcons name="bank" size={20} color="#fff" />
                  <Text style={{ fontSize:16, fontWeight:'700', color:'#fff' }}>Add Funding Round</Text>
                </LinearGradient>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* FINANCIAL FORECAST */}
      <Modal visible={showForecast} transparent animationType="slide" onRequestClose={() => setShowForecast(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="Financial Forecast" icon="chart-timeline" gradient={[T.indigo,T.purple]} onClose={() => setShowForecast(false)} />
            <ScrollView style={{ padding:20 }}>
              {[
                { label:'Revenue (£k)', data:(forecast?.revenue||[]),     color:T.primary  },
                { label:'Customers',    data:(forecast?.customers||[]),   color:T.success  },
                { label:'MRR (£k)',     data:(forecast?.mrr||[]),         color:T.teal     },
                { label:'Headcount',    data:(forecast?.headcount||[]),   color:T.warning  },
                { label:'Gross Margin %',data:(forecast?.grossMargin||[]),color:T.purple   },
              ].map((s,i) => {
                const max = Math.max(...s.data,1);
                return (
                  <View key={i} style={{ marginBottom:20 }}>
                    <Text style={{ fontSize:14, fontWeight:'700', color:T.dark, marginBottom:10 }}>{s.label}</Text>
                    <View style={{ flexDirection:'row', gap:8, alignItems:'flex-end', height:80 }}>
                      {s.data.map((v,j) => (
                        <View key={j} style={{ flex:1, alignItems:'center', gap:4 }}>
                          <View style={{ flex:1, width:'100%', justifyContent:'flex-end' }}>
                            <LinearGradient colors={[s.color+'60',s.color]} style={{ width:'100%', height:`${(v/max)*100}%`, borderRadius:6, minHeight:4 }} />
                          </View>
                          <Text style={{ fontSize:10, fontWeight:'800', color:s.color }}>{v.toLocaleString()}</Text>
                          <Text style={{ fontSize:9, color:T.gray }}>{(forecast?.years||[])[j]}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* MARKETS FULL */}
      <Modal visible={showMarkets} transparent animationType="slide" onRequestClose={() => setShowMarkets(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="Market Intelligence" icon="map-marker-multiple" gradient={[T.orange,T.warning]} onClose={() => setShowMarkets(false)} />
            <ScrollView style={{ padding:20 }}>
              {(markets||[]).map(m => {
                const sc = m.status==='Active'?T.success:m.status==='Exploring'?T.warning:T.primary;
                return (
                  <View key={m.id} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:12, borderWidth:1, borderColor:T.border }}>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <Text style={{ fontSize:16, fontWeight:'800', color:T.dark }}>{m.name}</Text>
                      <View style={{ backgroundColor:sc+'18', borderRadius:8, paddingHorizontal:10, paddingVertical:5 }}>
                        <Text style={{ fontSize:12, fontWeight:'700', color:sc }}>{m.status}</Text>
                      </View>
                    </View>
                    <View style={{ flexDirection:'row', gap:10, marginBottom:10 }}>
                      {[
                        { l:'Market Size',   v:m.size        },
                        { l:'Our Share',     v:m.share       },
                        { l:'Potential',     v:m.potential   },
                        { l:'Competition',   v:m.competition },
                      ].map((d,i) => (
                        <View key={i} style={{ flex:1, backgroundColor:T.bg, borderRadius:8, padding:8 }}>
                          <Text style={{ fontSize:9, color:T.gray }}>{d.l}</Text>
                          <Text style={{ fontSize:12, fontWeight:'800', color:T.dark }}>{d.v}</Text>
                        </View>
                      ))}
                    </View>
                    {m.notes ? <Text style={{ fontSize:12, color:T.gray, lineHeight:18 }}>{m.notes}</Text> : null}
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* COMPETITORS FULL */}
      <Modal visible={showCompetitors} transparent animationType="slide" onRequestClose={() => setShowCompetitors(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title="Competitor Analysis" icon="shield-half-full" gradient={[T.purple,T.pink]} onClose={() => setShowCompetitors(false)} />
            <ScrollView style={{ padding:20 }}>
              {(competitors||[]).map(c => {
                const tc = c.threat==='High'?T.danger:c.threat==='Medium'?T.warning:T.success;
                return (
                  <View key={c.id} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:12, borderWidth:1, borderColor:T.border }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:12, marginBottom:12 }}>
                      <View style={{ width:44, height:44, borderRadius:22, backgroundColor:T.primary+'18', alignItems:'center', justifyContent:'center' }}>
                        <Text style={{ fontSize:18, fontWeight:'800', color:T.primary }}>{c.name[0]}</Text>
                      </View>
                      <View style={{ flex:1 }}>
                        <Text style={{ fontSize:16, fontWeight:'800', color:T.dark }}>{c.name}</Text>
                        <Text style={{ fontSize:12, color:T.primary, fontWeight:'600' }}>{c.position}</Text>
                      </View>
                      <View style={{ backgroundColor:tc+'18', borderRadius:8, paddingHorizontal:8, paddingVertical:5 }}>
                        <Text style={{ fontSize:11, fontWeight:'800', color:tc }}>Threat: {c.threat}</Text>
                      </View>
                    </View>
                    {c.strengths?.length > 0 && (
                      <View style={{ marginBottom:8 }}>
                        <Text style={{ fontSize:11, fontWeight:'700', color:T.danger, marginBottom:5 }}>STRENGTHS</Text>
                        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6 }}>
                          {c.strengths.map((s,i) => <View key={i} style={{ backgroundColor:T.danger+'12', borderRadius:6, paddingHorizontal:8, paddingVertical:4 }}><Text style={{ fontSize:11, color:T.danger, fontWeight:'600' }}>{s}</Text></View>)}
                        </View>
                      </View>
                    )}
                    {c.weaknesses?.length > 0 && (
                      <View style={{ marginBottom:8 }}>
                        <Text style={{ fontSize:11, fontWeight:'700', color:T.success, marginBottom:5 }}>WEAKNESSES</Text>
                        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6 }}>
                          {c.weaknesses.map((w,i) => <View key={i} style={{ backgroundColor:T.success+'12', borderRadius:6, paddingHorizontal:8, paddingVertical:4 }}><Text style={{ fontSize:11, color:T.success, fontWeight:'600' }}>{w}</Text></View>)}
                        </View>
                      </View>
                    )}
                    {c.ourAdvantage ? (
                      <View style={{ backgroundColor:T.primary+'12', borderRadius:8, padding:10 }}>
                        <Text style={{ fontSize:11, fontWeight:'700', color:T.primary, marginBottom:3 }}>OUR ADVANTAGE</Text>
                        <Text style={{ fontSize:12, color:T.dark }}>{c.ourAdvantage}</Text>
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* TEAM PLAN */}
      <Modal visible={showTeamPlan} transparent animationType="slide" onRequestClose={() => setShowTeamPlan(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.85 }]}>
            <ModalHeader title="Team Scaling Plan" icon="account-group" gradient={[T.warning,T.orange]} onClose={() => setShowTeamPlan(false)} />
            <ScrollView style={{ padding:20 }}>
              <View style={{ flexDirection:'row', gap:10, marginBottom:20 }}>
                {[
                  { l:'Current',       v:String((teamData?.total||0)),           c:T.primary },
                  { l:'Growth Target', v:`+${(teamData?.growthTarget||0)}%`,     c:T.success },
                  { l:'Target Size',   v:String(Math.round((teamData?.total||0)*(1+(teamData?.growthTarget||0)/100))), c:T.orange },
                ].map((s,i) => (
                  <View key={i} style={{ flex:1, backgroundColor:s.c+'18', borderRadius:12, padding:14, alignItems:'center' }}>
                    <Text style={{ fontSize:22, fontWeight:'800', color:s.c }}>{s.v}</Text>
                    <Text style={{ fontSize:11, color:T.gray, marginTop:4 }}>{s.l}</Text>
                  </View>
                ))}
              </View>
              <Text style={S.modalSectionTitle}>Current Team Breakdown</Text>
              {[
                { l:'Engineering', v:(teamData?.engineering||0), t:Math.round((teamData?.engineering||0)*1.4), c:T.indigo  },
                { l:'Sales',       v:(teamData?.sales||0),       t:Math.round((teamData?.sales||0)*1.5),       c:T.success },
                { l:'Product',     v:(teamData?.product||0)||0,  t:Math.round(((teamData?.product||0)||0)*1.3), c:T.teal   },
                { l:'Operations',  v:(teamData?.operations||0)||0,t:Math.round(((teamData?.operations||0)||0)*1.2),c:T.orange},
                { l:'Finance',     v:(teamData?.finance||0)||0,  t:Math.round(((teamData?.finance||0)||0)*1.25),c:T.purple  },
              ].map((d,i) => {
                const max = Math.max(d.t,1);
                return (
                  <View key={i} style={{ marginBottom:16 }}>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:6 }}>
                      <Text style={{ fontSize:13, fontWeight:'700', color:T.dark }}>{d.l}</Text>
                      <Text style={{ fontSize:13, fontWeight:'800', color:d.c }}>{d.v} → {d.t}</Text>
                    </View>
                    <View style={{ flexDirection:'row', gap:4 }}>
                      <View style={{ height:10, backgroundColor:d.c, borderRadius:3, flex:d.v }} />
                      <View style={{ height:10, backgroundColor:d.c+'40', borderRadius:3, flex:d.t-d.v }} />
                      <View style={{ height:10, backgroundColor:T.border, borderRadius:3, flex:max-d.t }} />
                    </View>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginTop:4 }}>
                      <Text style={{ fontSize:10, color:d.c, fontWeight:'700' }}>Current: {d.v}</Text>
                      <Text style={{ fontSize:10, color:d.c+'80', fontWeight:'700' }}>+{d.t-d.v} hires needed</Text>
                    </View>
                  </View>
                );
              })}
              <Text style={[S.modalSectionTitle, { marginTop:8 }]}>Hiring Timeline</Text>
              {[
                { q:'Q1 2025', roles:['Senior ML Engineer x2', 'Enterprise Sales x1'], budget:'£180k' },
                { q:'Q2 2025', roles:['Engineering Lead x1', 'Sales x2', 'Country Manager (DE)'], budget:'£290k' },
                { q:'Q3 2025', roles:['Backend Engineers x3', 'Product Manager x1', 'Customer Success x2'], budget:'£340k' },
                { q:'Q4 2025', roles:['Frontend x2', 'Data Scientist x1', 'Legal Counsel x1'], budget:'£260k' },
              ].map((h,i) => (
                <View key={i} style={{ backgroundColor:'#fff', borderRadius:12, padding:14, marginBottom:10, borderWidth:1, borderColor:T.border }}>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:8 }}>
                    <Text style={{ fontSize:14, fontWeight:'800', color:T.primary }}>{h.q}</Text>
                    <Text style={{ fontSize:13, fontWeight:'800', color:T.success }}>{h.budget}</Text>
                  </View>
                  {h.roles.map((r,j) => (
                    <View key={j} style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:4 }}>
                      <View style={{ width:6, height:6, borderRadius:3, backgroundColor:T.primary }} />
                      <Text style={{ fontSize:13, color:T.dark }}>{r}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* AI ADVISOR */}
      <Modal visible={showAIAdvisor} transparent animationType="slide" onRequestClose={() => setShowAIAdvisor(false)}>
        <View style={S.modalOverlay}>
          <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
            <ModalHeader title={`${aiProvider} · Strategy Advisor`} icon="brain" gradient={[T.claude,T.primary]} onClose={() => { setShowAIAdvisor(false); setAiOutput(''); setAiPrompt(''); }} />
            <ScrollView style={{ padding:20 }}>
              <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:16 }}>
                {[
                  'Review my scale-up strategy',
                  'Identify top growth blockers',
                  'Prioritise my initiatives',
                  `Series A readiness check`,
                  'EU expansion playbook',
                  'Improve NPS & retention',
                  'Hiring plan for next 6 months',
                  'Competitor response strategy',
                ].map((p,i) => (
                  <TouchableOpacity key={i} onPress={() => setAiPrompt(p)}
                    style={{ backgroundColor:aiPrompt===p?T.claude+'20':T.bg, borderRadius:20, paddingHorizontal:12, paddingVertical:7, borderWidth:1, borderColor:aiPrompt===p?T.claude+'60':T.border }}>
                    <Text style={{ fontSize:12, fontWeight:'600', color:aiPrompt===p?T.claude:T.gray }}>{p}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
                <TextInput
                  value={aiPrompt} onChangeText={setAiPrompt}
                  placeholder="Ask anything about your scale-up strategy…"
                  placeholderTextColor="#94a3b8"
                  multiline numberOfLines={3}
                  style={[S.input, { flex:1, minHeight:70, textAlignVertical:'top', marginBottom:0 }]}
                />
              </View>
              <TouchableOpacity onPress={() => { if (aiPrompt.trim()) runAI(`You are an expert startup strategy advisor for ${companyName || 'our company'}.\n\nContext:\n- ARR: ${fmtMoney(metrics.revenue?.current)}, growing ${metrics.revenue?.growth}% YoY\n- MRR: ${fmtMoney(metrics.mrr?.current)}, ${metrics.customers?.current} customers\n- Team: ${(teamData?.total||0)} people, runway: ${metrics.runway} months\n- Active initiatives: ${activeCount}\n- Top risks: ${(risks||[]).filter(r=>r.level==='Critical').map(r=>r.title).join(', ')||'None critical'}\n- Vision: ${strategy.vision}\n\nUser question: ${aiPrompt}\n\nProvide specific, actionable advice with concrete next steps.`); }}
                disabled={strategyLoading || !aiPrompt.trim()}
                style={{ borderRadius:14, overflow:'hidden', marginBottom:16 }}>
                <LinearGradient colors={[T.claude,T.primary]} style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:14, gap:8 }}>
                  {strategyLoading ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="brain" size={20} color="#fff" />}
                  <Text style={{ fontSize:15, fontWeight:'700', color:'#fff' }}>{strategyLoading ? `${aiProvider} Thinking…` : 'Get Strategy Advice'}</Text>
                </LinearGradient>
              </TouchableOpacity>
              {aiOutput ? (
                <View style={{ backgroundColor:T.claude+'12', borderRadius:14, padding:16, borderWidth:1, borderColor:T.claude+'30', marginBottom:20 }}>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:12 }}>
                    <MaterialCommunityIcons name="brain" size={18} color={T.claude} />
                    <Text style={{ fontSize:13, fontWeight:'800', color:T.claude }}>{aiProvider}</Text>
                  </View>
                  <Text style={{ fontSize:14, color:T.dark, lineHeight:22 }}>{aiOutput}</Text>
                  <TouchableOpacity onPress={() => shareText(aiOutput, 'Strategy Advice')} style={{ flexDirection:'row', alignItems:'center', gap:6, marginTop:14 }}>
                    <Ionicons name="share-outline" size={16} color={T.claude} />
                    <Text style={{ fontSize:13, fontWeight:'700', color:T.claude }}>Share Advice</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </ScrollView>
          </View>
        </View>
      </Modal>

    </SafeAreaView>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────
function SectionHeader({ title, icon, gradient, action, onAction }) {
  return (
    <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, marginBottom:12 }}>
      <LinearGradient colors={gradient} style={{ width:32, height:32, borderRadius:10, alignItems:'center', justifyContent:'center', marginRight:10 }}>
        <MaterialCommunityIcons name={icon} size={18} color="#fff" />
      </LinearGradient>
      <Text style={{ fontSize:16, fontWeight:'800', color:T.dark, flex:1 }}>{title}</Text>
      {action && onAction && (
        <TouchableOpacity onPress={onAction} style={{ backgroundColor:T.primary+'12', borderRadius:10, paddingHorizontal:10, paddingVertical:6 }}>
          <Text style={{ fontSize:12, fontWeight:'700', color:T.primary }}>{action}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

function ModalHeader({ title, icon, gradient, onClose, action }) {
  return (
    <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:20, borderBottomWidth:1, borderBottomColor:T.border }}>
      <View style={{ flexDirection:'row', alignItems:'center', gap:12 }}>
        <LinearGradient colors={gradient} style={{ width:36, height:36, borderRadius:10, alignItems:'center', justifyContent:'center' }}>
          <MaterialCommunityIcons name={icon} size={20} color="#fff" />
        </LinearGradient>
        <Text style={{ fontSize:17, fontWeight:'800', color:T.dark }}>{title}</Text>
      </View>
      <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
        {action}
        <TouchableOpacity onPress={onClose} style={{ padding:4 }}>
          <Ionicons name="close" size={24} color={T.gray} />
        </TouchableOpacity>
      </View>
    </View>
  );
}

function MetricCard({ icon, label, value, target, growth, color, grad, inv }) {
  const good = inv ? growth <= 0 : growth >= 0;
  return (
    <View style={{ width:(width-44)/2, backgroundColor:'#fff', borderRadius:16, padding:14, borderWidth:1, borderColor:T.border }}>
      <View style={{ width:38, height:38, borderRadius:12, backgroundColor:color+'18', alignItems:'center', justifyContent:'center', marginBottom:10 }}>
        <Feather name={icon} size={18} color={color} />
      </View>
      <Text style={{ fontSize:20, fontWeight:'800', color:T.dark, marginBottom:3 }}>{value}</Text>
      <Text style={{ fontSize:11, color:T.gray, marginBottom:8 }}>{label}</Text>
      <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center' }}>
        <Text style={{ fontSize:10, color:T.gray }}>Target: {target}</Text>
        <View style={{ flexDirection:'row', alignItems:'center', gap:3, backgroundColor:(good?T.success:T.danger)+'15', paddingHorizontal:7, paddingVertical:3, borderRadius:10 }}>
          <Ionicons name={good?'arrow-up':'arrow-down'} size={10} color={good?T.success:T.danger} />
          <Text style={{ fontSize:10, fontWeight:'800', color:good?T.success:T.danger }}>{Math.abs(growth)}%</Text>
        </View>
      </View>
    </View>
  );
}

function EmptyState({ icon, title, sub, action, onAction }) {
  return (
    <View style={{ alignItems:'center', paddingVertical:32 }}>
      <View style={{ width:64, height:64, borderRadius:32, backgroundColor:T.primary+'12', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
        <Ionicons name={icon} size={30} color={T.primary} />
      </View>
      <Text style={{ fontSize:16, fontWeight:'700', color:T.dark, marginBottom:6 }}>{title}</Text>
      <Text style={{ fontSize:13, color:T.gray, marginBottom:18, textAlign:'center' }}>{sub}</Text>
      {action && <TouchableOpacity onPress={onAction} style={{ backgroundColor:T.primary, paddingHorizontal:20, paddingVertical:10, borderRadius:24 }}><Text style={{ color:'#fff', fontWeight:'700', fontSize:14 }}>{action}</Text></TouchableOpacity>}
    </View>
  );
}

function DetailRow({ label, value }) {
  return (
    <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingVertical:10, borderBottomWidth:1, borderBottomColor:T.border }}>
      <Text style={{ fontSize:12, fontWeight:'700', color:T.gray }}>{label.toUpperCase()}</Text>
      <Text style={{ fontSize:14, fontWeight:'700', color:T.dark }}>{value||'—'}</Text>
    </View>
  );
}

function BarChart({ data, color }) {
  const max = Math.max(...data.map(d=>d.value), 1);
  return (
    <View style={{ flexDirection:'row', gap:6, height:80, alignItems:'flex-end', marginBottom:8 }}>
      {data.map((d,i) => (
        <View key={i} style={{ flex:1, alignItems:'center', gap:4 }}>
          <View style={{ flex:1, width:'100%', justifyContent:'flex-end' }}>
            <LinearGradient colors={[color+'60', color]} style={{ width:'100%', height:`${(d.value/max)*100}%`, borderRadius:5, minHeight:4 }} />
          </View>
          <Text style={{ fontSize:11, fontWeight:'800', color:color }}>{d.value}</Text>
          <Text style={{ fontSize:9, color:T.gray }}>{d.label}</Text>
        </View>
      ))}
    </View>
  );
}

function AddInitiativeModal({ visible, onClose, form, setForm, onSubmit }) {
  const [loading, setLoading] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd]   = useState(false);
  const handle = async () => { setLoading(true); await onSubmit(); setLoading(false); };
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={S.modalOverlay}>
        <View style={[S.modalSheet, { maxHeight:height*0.92 }]}>
          <ModalHeader title="New Strategic Initiative" icon="rocket-outline" gradient={[T.primary,T.indigo]} onClose={onClose} />
          <ScrollView style={{ padding:20 }}>
            <TextInput placeholder="Initiative Title *" value={form.title} onChangeText={v=>setForm({...form,title:v})} style={S.input} placeholderTextColor="#94a3b8" />
            <Text style={S.label}>Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
              {INITIATIVE_TYPES.map(t => (
                <TouchableOpacity key={t.id} onPress={() => setForm({...form,type:t.id})} style={{ marginRight:8 }}>
                  <LinearGradient colors={form.type===t.id?t.gradient:['#f1f5f9','#e2e8f0']} style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:9, borderRadius:24, gap:7 }}>
                    <MaterialCommunityIcons name={t.icon} size={15} color={form.type===t.id?'#fff':T.gray} />
                    <Text style={{ fontSize:13, fontWeight:'700', color:form.type===t.id?'#fff':T.gray }}>{t.name}</Text>
                  </LinearGradient>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={S.label}>Growth Phase</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
              {GROWTH_PHASES.map(p => (
                <TouchableOpacity key={p.id} onPress={() => setForm({...form,phase:p.id})} style={{ marginRight:8 }}>
                  <LinearGradient colors={form.phase===p.id?p.gradient:['#f1f5f9','#e2e8f0']} style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:9, borderRadius:24, gap:7 }}>
                    <Ionicons name={p.icon} size={14} color={form.phase===p.id?'#fff':T.gray} />
                    <Text style={{ fontSize:13, fontWeight:'700', color:form.phase===p.id?'#fff':T.gray }}>{p.name}</Text>
                  </LinearGradient>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={S.label}>Priority</Text>
            <View style={{ flexDirection:'row', gap:8, marginBottom:16 }}>
              {PRIORITY_LEVELS.map(pr => (
                <TouchableOpacity key={pr.id} onPress={() => setForm({...form,priority:pr.id})} style={{ flex:1, paddingVertical:10, borderRadius:10, backgroundColor:form.priority===pr.id?pr.color:T.bg, borderWidth:1, borderColor:form.priority===pr.id?pr.color:T.border, alignItems:'center' }}>
                  <Text style={{ fontSize:12, fontWeight:'800', color:form.priority===pr.id?'#fff':T.gray }}>{pr.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput placeholder="Description" value={form.description} onChangeText={v=>setForm({...form,description:v})} multiline numberOfLines={3} style={[S.input,{minHeight:80,textAlignVertical:'top'}]} placeholderTextColor="#94a3b8" />
            <TextInput placeholder="Owner / Department" value={form.owner} onChangeText={v=>setForm({...form,owner:v})} style={S.input} placeholderTextColor="#94a3b8" />
            <TextInput placeholder="Budget (£)" value={form.budget} onChangeText={v=>setForm({...form,budget:v.replace(/[^0-9]/g,'')})} keyboardType="numeric" style={S.input} placeholderTextColor="#94a3b8" />
            <View style={{ flexDirection:'row', gap:12, marginBottom:16 }}>
              <TouchableOpacity onPress={() => setShowStart(true)} style={[S.input, { flex:1, flexDirection:'row', alignItems:'center', gap:8, marginBottom:0 }]}>
                <Ionicons name="calendar-outline" size={16} color={T.primary} />
                <Text style={{ fontSize:14, color:T.dark, flex:1 }}>{form.start_date ? new Date(form.start_date).toLocaleDateString('en-GB') : 'Start date'}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowEnd(true)} style={[S.input, { flex:1, flexDirection:'row', alignItems:'center', gap:8, marginBottom:0 }]}>
                <Ionicons name="calendar-outline" size={16} color={T.primary} />
                <Text style={{ fontSize:14, color:T.dark, flex:1 }}>{form.end_date ? new Date(form.end_date).toLocaleDateString('en-GB') : 'End date'}</Text>
              </TouchableOpacity>
            </View>
            <Text>{showStart && <DateTimePicker value={form.start_date} mode="date" display={Platform.OS==='ios'?'spinner':'default'} onChange={(e,d)=>{ setShowStart(false); if(d) setForm({...form,start_date:d}); }} />}</Text>
            <Text>{showEnd   && <DateTimePicker value={form.end_date}   mode="date" display={Platform.OS==='ios'?'spinner':'default'} onChange={(e,d)=>{ setShowEnd(false);   if(d) setForm({...form,end_date:d});   }} />}</Text>
            <TextInput placeholder="Success Metrics" value={form.success_metrics} onChangeText={v=>setForm({...form,success_metrics:v})} style={S.input} placeholderTextColor="#94a3b8" />
            <TextInput placeholder="Key Risks" value={form.risks} onChangeText={v=>setForm({...form,risks:v})} style={[S.input,{marginBottom:24}]} placeholderTextColor="#94a3b8" />
            <TouchableOpacity onPress={handle} disabled={loading} style={{ borderRadius:14, overflow:'hidden', marginBottom:20 }}>
              <LinearGradient colors={[T.primary,T.indigo]} style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:16, gap:8 }}>
                {loading ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="rocket-outline" size={20} color="#fff" />}
                <Text style={{ fontSize:16, fontWeight:'700', color:'#fff' }}>Create Initiative</Text>
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function AddMilestoneModal({ visible, onClose, form, setForm, onSubmit }) {
  const [loading, setLoading] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const handle = async () => { setLoading(true); await onSubmit(); setLoading(false); };
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={S.modalOverlay}>
        <View style={[S.modalSheet, { maxHeight:height*0.8 }]}>
          <ModalHeader title="Add Milestone" icon="flag-outline" gradient={[T.teal,T.success]} onClose={onClose} />
          <ScrollView style={{ padding:20 }}>
            <TextInput placeholder="Milestone Title *" value={form.title} onChangeText={v=>setForm({...form,title:v})} style={S.input} placeholderTextColor="#94a3b8" />
            <Text style={S.label}>Growth Phase</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
              {GROWTH_PHASES.map(p => (
                <TouchableOpacity key={p.id} onPress={() => setForm({...form,phase:p.id})} style={{ marginRight:8 }}>
                  <LinearGradient colors={form.phase===p.id?p.gradient:['#f1f5f9','#e2e8f0']} style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:9, borderRadius:24, gap:7 }}>
                    <Ionicons name={p.icon} size={14} color={form.phase===p.id?'#fff':T.gray} />
                    <Text style={{ fontSize:13, fontWeight:'700', color:form.phase===p.id?'#fff':T.gray }}>{p.name}</Text>
                  </LinearGradient>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={S.label}>Priority</Text>
            <View style={{ flexDirection:'row', gap:8, marginBottom:16 }}>
              {PRIORITY_LEVELS.map(pr => (
                <TouchableOpacity key={pr.id} onPress={() => setForm({...form,priority:pr.id})} style={{ flex:1, paddingVertical:10, borderRadius:10, backgroundColor:form.priority===pr.id?pr.color:T.bg, borderWidth:1, borderColor:form.priority===pr.id?pr.color:T.border, alignItems:'center' }}>
                  <Text style={{ fontSize:12, fontWeight:'800', color:form.priority===pr.id?'#fff':T.gray }}>{pr.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity onPress={() => setShowDate(true)} style={[S.input, { flexDirection:'row', alignItems:'center', gap:8 }]}>
              <Ionicons name="calendar-outline" size={16} color={T.teal} />
              <Text style={{ fontSize:14, color:T.dark }}>{form.target_date ? new Date(form.target_date).toLocaleDateString('en-GB') : 'Target date'}</Text>
            </TouchableOpacity>
            <Text>{showDate && <DateTimePicker value={form.target_date} mode="date" display={Platform.OS==='ios'?'spinner':'default'} onChange={(e,d) => { setShowDate(false); if(d) setForm({...form,target_date:d}); }} />}</Text>
            <TextInput placeholder="Description" value={form.description} onChangeText={v=>setForm({...form,description:v})} multiline numberOfLines={3} style={[S.input,{minHeight:72,textAlignVertical:'top'}]} placeholderTextColor="#94a3b8" />
            <TextInput placeholder="Success Criteria" value={form.success_criteria} onChangeText={v=>setForm({...form,success_criteria:v})} style={[S.input,{marginBottom:24}]} placeholderTextColor="#94a3b8" />
            <TouchableOpacity onPress={handle} disabled={loading} style={{ borderRadius:14, overflow:'hidden', marginBottom:20 }}>
              <LinearGradient colors={[T.teal,T.success]} style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:16, gap:8 }}>
                {loading ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="flag-outline" size={20} color="#fff" />}
                <Text style={{ fontSize:16, fontWeight:'700', color:'#fff' }}>Create Milestone</Text>
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function StrategyModal({ visible, onClose, strategy }) {
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={S.modalOverlay}>
        <View style={[S.modalSheet, { height:height*0.85 }]}>
          <ModalHeader title="Vision & Strategy" icon="rocket-launch" gradient={[T.primary,T.indigo]} onClose={onClose} />
          <ScrollView style={{ padding:20 }}>
            {[
              { title:'🔭 Vision',         text:strategy.vision,   color:T.primary, icon:'eye-outline'    },
              { title:'🎯 Mission',         text:strategy.mission,  color:T.success, icon:'flag-outline'   },
            ].map((s,i) => (
              <View key={i} style={{ marginBottom:20 }}>
                <LinearGradient colors={[s.color+'12',s.color+'06']} style={{ borderRadius:12, padding:14, borderLeftWidth:4, borderLeftColor:s.color }}>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:8 }}>
                    <Ionicons name={s.icon} size={18} color={s.color} />
                    <Text style={{ fontSize:15, fontWeight:'800', color:T.dark }}>{s.title}</Text>
                  </View>
                  <Text style={{ fontSize:14, color:T.gray, lineHeight:22 }}>{s.text}</Text>
                </LinearGradient>
              </View>
            ))}
            <View style={{ marginBottom:20 }}>
              <Text style={{ fontSize:15, fontWeight:'800', color:T.dark, marginBottom:12 }}>💎 Core Values</Text>
              <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                {strategy.values?.map((v,i) => (
                  <LinearGradient key={i} colors={[T.purple+'18',T.pink+'18']} style={{ borderRadius:20, paddingHorizontal:14, paddingVertical:8, borderWidth:1, borderColor:T.purple+'30' }}>
                    <Text style={{ fontSize:13, fontWeight:'700', color:T.purple }}>{v}</Text>
                  </LinearGradient>
                ))}
              </View>
            </View>
            <View>
              <Text style={{ fontSize:15, fontWeight:'800', color:T.dark, marginBottom:12 }}>🏆 Long-term Goals</Text>
              {strategy.goals?.map((g,i) => (
                <View key={i} style={{ flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:12 }}>
                  <LinearGradient colors={[T.success,T.teal]} style={{ width:22, height:22, borderRadius:7, alignItems:'center', justifyContent:'center', marginTop:1 }}>
                    <Ionicons name="checkmark" size={13} color="#fff" />
                  </LinearGradient>
                  <Text style={{ flex:1, fontSize:14, color:T.dark, lineHeight:22 }}>{g}</Text>
                </View>
              ))}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  container:    { flex:1, backgroundColor:T.bg },
  loadingWrap:  { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#fff' },
  loadingIcon:  { width:96, height:96, borderRadius:48, alignItems:'center', justifyContent:'center', marginBottom:24 },
  loadingTitle: { fontSize:22, fontWeight:'800', color:T.dark, marginTop:16 },
  loadingSub:   { fontSize:14, color:T.gray, marginTop:6 },

  header:       { position:'absolute', top:0, left:0, right:0, zIndex:1000, elevation:4 },
  headerGrad:   { paddingTop:Platform.OS==='ios'?52:24, paddingBottom:16, paddingHorizontal:20 },
  headerRow:    { flexDirection:'row', alignItems:'center', marginBottom:12 },
  headerBtn:    { width:40, height:40, borderRadius:12, backgroundColor:'rgba(255,255,255,0.2)', alignItems:'center', justifyContent:'center' },
  headerTitle:  { fontSize:20, fontWeight:'800', color:'#fff' },
  headerSub:    { fontSize:11, color:'rgba(255,255,255,0.8)', marginTop:1 },
  statsStrip:   { flexDirection:'row', backgroundColor:'rgba(255,255,255,0.18)', borderRadius:14, padding:14, marginTop:6 },

  errBanner:    { backgroundColor:T.danger, flexDirection:'row', alignItems:'center', padding:12, marginTop:78, marginHorizontal:16, borderRadius:12, gap:8 },

  statsRow:     { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:16, gap:12, marginBottom:12 },
  statCard:     { width:(width-44)/2, padding:16, borderRadius:16, alignItems:'flex-start' },

  metricsGrid:  { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:16, gap:12, marginBottom:12 },

  finGrid:      { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:16, gap:12, marginBottom:16 },
  finCard:      { width:(width-56)/3, backgroundColor:'#fff', borderRadius:14, padding:12, borderWidth:1, borderColor:T.border, alignItems:'flex-start' },
  finIcon:      { width:36, height:36, borderRadius:10, alignItems:'center', justifyContent:'center', marginBottom:8 },
  finLabel:     { fontSize:10, color:T.gray, marginBottom:4 },
  finValue:     { fontSize:17, fontWeight:'800', marginBottom:2 },
  finSub:       { fontSize:10, color:T.gray },

  msItem:       { flexDirection:'row', alignItems:'center', gap:12, backgroundColor:'#fff', borderRadius:12, padding:12, marginBottom:8, borderWidth:1, borderColor:T.border },
  msCk:         { width:28, height:28, borderRadius:8, alignItems:'center', justifyContent:'center', borderWidth:2 },
  msTitle:      { fontSize:14, fontWeight:'700', color:T.dark },

  initCard:     { backgroundColor:'#fff', borderRadius:14, marginBottom:12, borderWidth:1, borderColor:T.border, overflow:'hidden' },
  initTitle:    { fontSize:15, fontWeight:'800', color:T.dark },
  initDesc:     { fontSize:12, color:T.gray, lineHeight:18, marginBottom:10 },

  compCard:     { backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:10, borderWidth:1, borderColor:T.border },

  teamGrid:     { flexDirection:'row', flexWrap:'wrap', gap:12 },
  teamCard:     { width:(width-68)/3, backgroundColor:'#fff', borderRadius:12, padding:12, borderWidth:1, borderColor:T.border, alignItems:'center' },

  fab:          { position:'absolute', bottom:30, right:20, zIndex:1000, elevation:8 },
  fabGrad:      { width:60, height:60, borderRadius:30, alignItems:'center', justifyContent:'center', shadowColor:T.primary, shadowOffset:{width:0,height:4}, shadowOpacity:0.35, shadowRadius:8 },

  modalOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.52)', justifyContent:'flex-end' },
  modalSheet:   { backgroundColor:'#fff', borderTopLeftRadius:28, borderTopRightRadius:28 },
  modalSectionTitle: { fontSize:14, fontWeight:'800', color:T.dark, marginBottom:12 },

  input:        { borderWidth:1, borderColor:T.border, borderRadius:14, paddingHorizontal:16, paddingVertical:13, fontSize:14, color:T.dark, backgroundColor:'#fff', marginBottom:14 },
  label:        { fontSize:13, fontWeight:'700', color:T.dark, marginBottom:8 },
});